/**
 * CareerOrientationPage.tsx
 * Career Orientation Tool - Self-assessment tool for career direction
 * Redesigned with consistent Header/Footer and modern UI
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getCanonicalUrl, getSiteUrl } from '../utils/siteConfig';
import { SEOHead } from '../components/SEOHead';
import { StructuredData } from '../components/StructuredData';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@vsat/shared/components';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  Lightbulb,
  ListOrdered,
  Loader2,
  RotateCcw,
  School,
  Sparkles,
  Target,
} from 'lucide-react';
import { ModernPaymentDialog } from '../components/payment/ModernPaymentDialog';
import {
  apiService,
  type CareerOrientationGroupWithQuestions,
  DEFAULT_CAT_REPORT_PRICE,
} from '@vsat/shared/lib/api';
import { toast } from 'sonner';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { Logo } from '../components/Logo';
import { ReportLayout } from '../components/career-orientation/full-report/ReportLayout';
import { SendEmailModal } from '../components/career-orientation/full-report/SendEmailModal';
import { buildFullReportViewModel } from '../components/career-orientation/full-report/useFullReportViewModel';
import {
  trackCATStart,
  trackCATProgress,
  trackCATComplete,
  trackViewFreeResult,
  trackBeginCheckout,
  trackViewFullReport,
  trackDownloadReport,
  trackSendReportEmail,
} from '../telemetry/googleAnalytics';

type Screen = 'intro' | 'instruction' | 'questions' | 'free-result' | 'payment' | 'full-report';

// New Loading Overlay Component
const LoadingOverlay = ({ message }: { message: string }) => (
  <div className='fixed inset-0 z-50 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-sm flex items-center justify-center'>
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className='bg-white dark:bg-surface-dark p-8 rounded-2xl shadow-xl flex flex-col items-center max-w-sm w-full mx-4 border border-border-light dark:border-border-dark'
    >
      <div className='relative mb-6'>
        <div className='w-16 h-16 rounded-full border-4 border-primary/20 animate-spin border-t-primary'></div>
        <div className='absolute inset-0 flex items-center justify-center'>
          <Sparkles className='w-6 h-6 text-primary animate-pulse' />
        </div>
      </div>
      <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2 text-center'>
        Đang xử lý
      </h3>
      <p className='text-gray-500 dark:text-gray-400 text-center text-sm animate-pulse'>
        {message}
      </p>
    </motion.div>
  </div>
);

// Custom Header for Career Orientation Page (matching design reference)
function CareerOrientationHeader({
  onLogoClick,
  showStartButton = false,
  onStartClick,
}: {
  onLogoClick?: () => void;
  showStartButton?: boolean;
  onStartClick?: () => void;
}) {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      navigate('/');
    }
  };

  return (
    <header className='sticky top-0 z-50 w-full bg-white border-b'>
      <div className='container mx-auto px-4'>
        <div className='flex h-16 md:h-20 items-center justify-between'>
          {/* Left: Logo + Branding */}
          <button
            onClick={handleLogoClick}
            className='flex items-center gap-3 hover:opacity-80 transition-opacity'
          >
            <Logo size='md' logoPath='/logo-dinh-huong-nghe-nghiep.png' />
            <div className='flex flex-col'>
              <span className='text-lg md:text-xl font-bold text-primary'>
                Hướng nghiệp CAT - Career Aptitude Test
              </span>
              <span className='text-xs md:text-sm text-gray-600 hidden sm:block'>
                Viện Khảo thí và Đánh giá chất lượng giáo dục
              </span>
            </div>
          </button>

          {/* Right: Home Link or Start Button */}
          {showStartButton && onStartClick ? (
            <Button onClick={onStartClick} className='bg-primary hover:bg-primary/90'>
              Bắt đầu
            </Button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className='text-sm md:text-base font-medium text-gray-700 hover:text-primary transition-colors'
            >
              Trang chủ THPT
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export default function CareerOrientationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const screenFromUrl = searchParams.get('screen') as Screen | null;
  const sessionIdFromUrl = searchParams.get('sessionId');
  const bypassCode = searchParams.get('bypass');

  // IEQTE bypass code for testing team to skip payment
  // Format: ?bypass=VKT-X9f2Kp7mQz3W
  const isTestBypassActive = bypassCode === 'VKT-X9f2Kp7mQz3W';
  const validScreens: Screen[] = [
    'intro',
    'instruction',
    'questions',
    'free-result',
    'payment',
    'full-report',
  ];

  const initialScreen =
    screenFromUrl && validScreens.includes(screenFromUrl) ? screenFromUrl : 'instruction';

  const [currentScreen, setCurrentScreen] = useState<Screen>(initialScreen);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [selectedLevel, setSelectedLevel] = useState<'THCS' | 'THPT' | null>(null);
  const [scores, setScores] = useState<Array<{ id: string; score: number; name: string }>>([]);
  const [topGroups, setTopGroups] = useState<Array<{ id: string; score: number; name: string }>>(
    []
  );
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showLevelSelection, setShowLevelSelection] = useState(false);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<Screen | null>(null);
  const [showTestStructure, setShowTestStructure] = useState(false);
  const isOnQuestionsScreen = currentScreen === 'questions';

  // API data states
  const [groups, setGroups] = useState<CareerOrientationGroupWithQuestions[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [assessmentId, setAssessmentId] = useState<string | null>(sessionIdFromUrl);
  const [groupsLoadError, setGroupsLoadError] = useState<string | null>(null);
  const [fullReportData, setFullReportData] = useState<
    CareerOrientationGroupWithQuestions[] | null
  >(null);
  const [loadingFullReport, setLoadingFullReport] = useState(false);
  const [userProfile, setUserProfile] = useState<{ name: string; class: string } | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user, isAuthenticated, accessToken } = useAuthStore();
  const paymentSuccessProcessedRef = useRef<string | null>(null);
  const [catReportPrice, setCatReportPrice] = useState<number>(DEFAULT_CAT_REPORT_PRICE);
  // Teaser titles for top group in free report (to encourage CTA)
  const [topGroupTeaserTitles, setTopGroupTeaserTitles] = useState<string[]>([]);

  // Loading states for transitions
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Protect question-related screens for authenticated users only
  useEffect(() => {
    const guardedScreens: Screen[] = ['questions', 'payment', 'free-result', 'full-report'];
    if (guardedScreens.includes(currentScreen) && (!isAuthenticated || !accessToken)) {
      const redirectUrl = `/huong-nghiep-cat?screen=${currentScreen}`;
      localStorage.setItem('redirectAfterLogin', redirectUrl);
      navigate('/login', { replace: true });
    }
  }, [currentScreen, isAuthenticated, accessToken, navigate]);

  // Initialize URL param on mount
  useEffect(() => {
    if (!searchParams.get('screen')) {
      setSearchParams({ screen: initialScreen }, { replace: true });
    }
  }, []);

  // Load CAT report price from config
  useEffect(() => {
    const loadPrice = async () => {
      try {
        const price = await apiService.getCATReportPrice();
        setCatReportPrice(price);
      } catch (error) {
        console.warn('Failed to load CAT report price, using default:', DEFAULT_CAT_REPORT_PRICE);
      }
    };
    loadPrice();
  }, []);

  // Sync currentScreen with URL params
  useEffect(() => {
    const urlScreen = searchParams.get('screen') as Screen | null;
    if (urlScreen && validScreens.includes(urlScreen) && urlScreen !== currentScreen) {
      setCurrentScreen(urlScreen);
      if (urlScreen === 'intro') {
        setCurrentGroupIndex(0);
        setAnswers({});
        setScores([]);
        setTopGroups([]);
        setSelectedLevel(null);
        setAssessmentId(null);
        setFullReportData(null);
      }
    }
  }, [searchParams, currentScreen, validScreens, setSearchParams]);

  // Handle navigation confirmation
  const handleConfirmNavigation = () => {
    if (pendingNavigation) {
      setCurrentScreen(pendingNavigation);
      setSearchParams({ screen: pendingNavigation }, { replace: true });
      if (pendingNavigation === 'intro') {
        setCurrentGroupIndex(0);
        setAnswers({});
        setScores([]);
        setTopGroups([]);
        setSelectedLevel(null);
        setAssessmentId(null);
        setFullReportData(null);
      }
    }
    setShowNavigationConfirm(false);
    setPendingNavigation(null);
  };

  const handleCancelNavigation = () => {
    setShowNavigationConfirm(false);
    setPendingNavigation(null);
  };

  // Handle logo click with confirmation (only show popup if on questions screen)
  const handleLogoClick = () => {
    if (isOnQuestionsScreen) {
      setPendingNavigation('intro');
      setShowNavigationConfirm(true);
    } else {
      updateScreen('intro');
    }
  };

  // Update URL when currentScreen changes
  const updateScreen = useCallback(
    (newScreen: Screen, sessionIdOverride?: string | null) => {
      const sessionIdToUse = sessionIdOverride || assessmentId || undefined;
      setCurrentScreen(newScreen);
      const params: Record<string, string> = { screen: newScreen };
      if (sessionIdToUse) {
        params.sessionId = sessionIdToUse;
      }
      // Preserve bypass param if it exists
      if (bypassCode) {
        params.bypass = bypassCode;
      }
      setSearchParams(params, { replace: true });
    },
    [assessmentId, bypassCode, setSearchParams]
  );

  // Browser beforeunload warning
  useEffect(() => {
    if (isOnQuestionsScreen) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
        return '';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isOnQuestionsScreen]);

  // Load groups automatically when entering questions screen (if not already loaded)
  useEffect(() => {
    if (
      currentScreen === 'questions' &&
      groups.length === 0 &&
      !loadingGroups &&
      !groupsLoadError
    ) {
      console.log('[CareerOrientation] Auto-loading session for questions screen');
      if (assessmentId) {
        loadAssessmentQuestions(assessmentId).catch(error => {
          console.error('[CareerOrientation] Failed to auto-load assessment session:', error);
        });
      } else {
        loadGroups().catch(error => {
          console.error('[CareerOrientation] Failed to auto-load groups:', error);
        });
      }
    }
  }, [currentScreen, groups.length, loadingGroups, groupsLoadError, assessmentId]);

  // Background autosave every 30s while on questions screen
  useEffect(() => {
    if (!assessmentId || currentScreen !== 'questions') return;
    const intervalId = window.setInterval(() => {
      try {
        const allAnswers = groups.flatMap(
          group =>
            group.questions?.map(q => ({
              questionId: q.id,
              answer: answers[q.id] ?? false,
            })) || []
        );
        if (allAnswers.length === 0) return;
        apiService.submitCareerOrientationAnswers(assessmentId, allAnswers).catch(error => {
          console.error('[CareerOrientation] Timed auto-save failed:', error);
        });
      } catch (error) {
        console.error('[CareerOrientation] Timed auto-save error:', error);
      }
    }, 30000);
    return () => window.clearInterval(intervalId);
  }, [assessmentId, currentScreen, groups, answers]);

  // Load free-result data when entering via session link
  useEffect(() => {
    if ((currentScreen === 'free-result' || currentScreen === 'payment') && assessmentId) {
      const missingData = groups.length === 0 || topGroups.length === 0 || scores.length === 0;
      if (missingData) {
        loadFreeResult(assessmentId);
      }
    }
  }, [currentScreen, assessmentId, groups.length, topGroups.length, scores.length]);

  // Load full-report data when entering via session link
  useEffect(() => {
    if (currentScreen === 'full-report' && assessmentId) {
      const missingData =
        !fullReportData ||
        fullReportData.length === 0 ||
        scores.length === 0 ||
        groups.length === 0;
      if (missingData) {
        loadFullReportForSession(assessmentId);
      }
    }
  }, [currentScreen, assessmentId, fullReportData, scores.length, groups.length]);

  // Open payment dialog automatically when on payment screen with session
  // BUT: If bypass code is active, auto-grant access instead
  // NOTE: FREE-RESULT screen does NOT auto-open payment - user must click button
  useEffect(() => {
    if (currentScreen === 'payment' && assessmentId) {
      if (isTestBypassActive) {
        // Enforce education level selection before enabling bypass
        // This ensures the report is generated correctly for THCS or THPT
        if (!selectedLevel) {
          // Wait for data valid state before showing dialog
          if (!loadingFullReport && !loadingGroups) {
            console.log(
              '[CareerOrientation] Bypass active but level missing. Prompting selection.'
            );
            setShowLevelSelection(true);
          }
          return;
        }

        // Bypass payment for IEQTE testing team - directly grant access
        const grantBypassAccess = async () => {
          try {
            console.log('[CareerOrientation] IEQTE bypass - granting access directly');
            console.log('[Debug] assessmentId:', assessmentId);
            console.log('[Debug] isTestBypassActive:', isTestBypassActive);
            console.log('[Debug] currentScreen:', currentScreen);
            console.log('[Debug] selectedLevel:', selectedLevel);

            toast.success('Chế độ kiểm thử IEQTE: Bỏ qua thanh toán');
            await apiService.grantCareerOrientationFullReportAccess(assessmentId);

            // Load full report data
            const fullReport = await apiService.getCareerOrientationFullReport(assessmentId);
            const fullReportGroups = fullReport.groupResults
              .sort((a, b) => a.rank - b.rank)
              .map(gr => ({
                ...gr.group,
                questions: gr.group.questions || [],
              })) as CareerOrientationGroupWithQuestions[];
            setFullReportData(fullReportGroups);
            updateScreen('full-report', assessmentId);
          } catch (error) {
            console.error('[CareerOrientation] Bypass grant failed:', error);
            toast.error('Không thể kích hoạt chế độ kiểm thử. Vui lòng thử lại.');
          }
        };
        grantBypassAccess();
      } else {
        setIsPaymentDialogOpen(true);
      }
    }
  }, [
    currentScreen,
    assessmentId,
    isTestBypassActive,
    selectedLevel,
    loadingFullReport,
    loadingGroups,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user profile for PDF export
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const profile = await apiService.getProfile();
        const userName =
          user?.name ||
          `${profile.firstName || ''} ${profile.lastName || ''}`.trim() ||
          profile.email?.split('@')[0] ||
          'Học sinh';
        const userClass = profile.gradeLevel || profile.class || 'N/A';
        setUserProfile({ name: userName, class: userClass });
      } catch (error) {
        console.error('Failed to load user profile:', error);
        const userName = user?.name || 'Học sinh';
        const userClass = 'N/A';
        setUserProfile({ name: userName, class: userClass });
      }
    };

    if (currentScreen === 'full-report') {
      loadUserProfile();
    }
  }, [currentScreen, user]);

  // For free-result and full-report, if no groups but no error, redirect to intro
  // (user might have navigated directly without completing assessment)
  // BUT: Don't redirect if we have assessmentId - we're loading data from session
  useEffect(() => {
    if (
      (currentScreen === 'free-result' || currentScreen === 'full-report') &&
      groups.length === 0 &&
      !loadingGroups &&
      !loadingFullReport &&
      !groupsLoadError &&
      !assessmentId // Only redirect if no assessmentId - if we have one, we're loading from session
    ) {
      updateScreen('intro');
    }
  }, [
    currentScreen,
    groups.length,
    loadingGroups,
    loadingFullReport,
    groupsLoadError,
    assessmentId,
    updateScreen,
  ]);

  // Load groups and questions from API
  const loadGroups = async () => {
    try {
      setLoadingGroups(true);
      setGroupsLoadError(null);
      const data = await apiService.getCareerOrientationPublicGroups();
      console.log('[CareerOrientation] Loaded groups:', data?.length || 0, 'groups');

      if (!data || data.length === 0) {
        const errorMessage = 'Không tìm thấy dữ liệu câu hỏi. Vui lòng liên hệ quản trị viên.';
        setGroupsLoadError(errorMessage);
        toast.error(errorMessage);
        setLoadingGroups(false);
        throw new Error(errorMessage);
      }

      const sortedGroups = data.sort((a, b) => a.order - b.order);
      console.log('[CareerOrientation] Setting groups:', sortedGroups.length);
      setGroups(sortedGroups);
    } catch (error: any) {
      console.error('[CareerOrientation] Failed to load groups:', error);
      const errorMessage =
        error?.message || 'Không thể tải danh sách câu hỏi. Vui lòng thử lại sau.';
      setGroupsLoadError(errorMessage);
      toast.error(errorMessage);
      setLoadingGroups(false);
      throw error;
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadFreeResult = async (sessionId: string) => {
    try {
      setLoadingGroups(true);
      const result = await apiService.getCareerOrientationAssessmentResult(sessionId);
      setAssessmentId(sessionId);

      // Get ALL 6 groups from scores (not just top 3 from groupResults)
      // This ensures consistent display with immediate completion view
      const allGroupsFromScores = (result.scores || []).map((score: any) => ({
        ...score.group,
        questions: score.group?.questions || [],
      })) as CareerOrientationGroupWithQuestions[];

      // Sort by group order to maintain consistent display
      const sortedAllGroups = allGroupsFromScores.sort(
        (a: any, b: any) => (a.order || 0) - (b.order || 0)
      );
      setGroups(sortedAllGroups);

      // Set education level from API response
      if (result.educationLevel) {
        setSelectedLevel(result.educationLevel);
      }

      const computedScores =
        result.scores?.map(score => ({
          id: score.groupId,
          score: score.score,
          name: score.group.name,
        })) || [];
      setScores(computedScores);

      // Top groups: Use groupResults array which has correct rank from backend
      // Sort by rank ASC to ensure correct order
      const sortedGroupResults = (result.groupResults || []).sort(
        (a: any, b: any) => a.rank - b.rank
      );

      const computedTopGroups = sortedGroupResults.slice(0, 3).map((groupResult: any) => ({
        id: groupResult.groupId,
        score: groupResult.score,
        name: groupResult.group.name,
      }));
      setTopGroups(computedTopGroups);

      // Extract teaser titles from top ranked group (rank 1) for CTA display
      const topGroupResult = result.groupResults?.[0];
      const groupWithTeaser = topGroupResult?.group as any;
      if (groupWithTeaser?.fullDescTeaserTitles) {
        setTopGroupTeaserTitles(groupWithTeaser.fullDescTeaserTitles);
      } else {
        setTopGroupTeaserTitles([]);
      }

      // ── Payment Recovery ──
      // Handles case: user paid + navigated away before polling completed.
      // BIDV already called /paybill → backend set hasFullReportAccess = true.
      // When user returns to free-result screen, we detect this and jump straight
      // to the full-report instead of showing the locked/payment screen again.
      if (result.hasFullReportAccess) {
        console.log(
          '[CareerOrientation] Detected prior payment success (hasFullReportAccess=true). Redirecting to full-report.'
        );
        toast.success('Tài khoản của bạn đã được mở khóa! Đang tải báo cáo chi tiết...');
        setTimeout(() => updateScreen('full-report', sessionId), 300);
        return;
      }
    } catch (error: any) {
      console.error('[CareerOrientation] Failed to load free result:', error);
      toast.error('Không thể tải kết quả miễn phí. Vui lòng thử lại.');
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadFullReportForSession = async (sessionId: string) => {
    try {
      setLoadingFullReport(true);

      // Try full report first, redirect to free-result if no access
      let reportData: any;
      try {
        reportData = await apiService.getCareerOrientationFullReport(sessionId);
      } catch (fullReportError: any) {
        // If 409 (Conflict - requires payment) or 403, redirect to free-result
        if (
          fullReportError?.message?.includes('payment') ||
          fullReportError?.message?.includes('409') ||
          fullReportError?.message?.includes('403')
        ) {
          console.log(
            '[CareerOrientation] Full report requires payment, redirecting to free-result'
          );
          toast.info('Vui lòng thanh toán để xem báo cáo chi tiết');
          setLoadingFullReport(false);
          updateScreen('free-result', sessionId);
          return;
        } else {
          throw fullReportError;
        }
      }

      // Get ALL groups from scores (not just top 3 from groupResults)
      // This ensures compass shows all 6 Holland groups
      const allGroupsFromScores = (reportData.scores || []).map((score: any) => ({
        ...score.group,
        questions: score.group?.questions || [],
      })) as CareerOrientationGroupWithQuestions[];

      // Sort by group order to maintain consistent display
      const sortedAllGroups = allGroupsFromScores.sort(
        (a: any, b: any) => (a.order || 0) - (b.order || 0)
      );

      setFullReportData(sortedAllGroups);
      setGroups(sortedAllGroups);

      // Set education level from API response
      if (reportData.educationLevel) {
        setSelectedLevel(reportData.educationLevel);
      }

      const computedScores =
        reportData.scores?.map((score: any) => ({
          id: score.groupId,
          score: score.score,
          name: score.group.name,
        })) || [];
      setScores(computedScores);

      // Top groups: Use groupResults array which has correct rank from backend
      // Sort by rank ASC to ensure correct order
      const sortedGroupResults = (reportData.groupResults || []).sort(
        (a: any, b: any) => a.rank - b.rank
      );

      const computedTopGroups = sortedGroupResults.slice(0, 3).map((groupResult: any) => ({
        id: groupResult.groupId,
        score: groupResult.score,
        name: groupResult.group.name,
      }));
      setTopGroups(computedTopGroups);
    } catch (error: any) {
      console.error('[CareerOrientation] Failed to load report by session:', error);
      toast.error('Không thể tải báo cáo. Vui lòng thử lại.');
    } finally {
      setLoadingFullReport(false);
    }
  };

  const loadAssessmentQuestions = async (sessionId: string) => {
    setLoadingGroups(true);
    setGroupsLoadError(null);
    try {
      const sessionData = await apiService.getCareerOrientationAssessmentQuestions(sessionId);
      const sortedGroups = (sessionData.groups || []).sort((a, b) => a.order - b.order);
      setGroups(sortedGroups);
      const restoredAnswers: Record<string, boolean> = {};
      sessionData.answers?.forEach(a => {
        restoredAnswers[a.questionId] = a.answer;
      });
      setAnswers(prev => ({ ...prev, ...restoredAnswers }));
      setAssessmentId(sessionData.assessmentId);
      setCurrentGroupIndex(0);
    } catch (error: any) {
      console.error('[CareerOrientation] Failed to load assessment session:', error);
      const errorMessage = error?.message || 'Không thể tải phiên làm bài. Vui lòng thử lại.';
      setGroupsLoadError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleAnswerChange = async (_groupId: string, newAnswers: Record<string, boolean>) => {
    // Update local state immediately
    setAnswers(prev => ({
      ...prev,
      ...newAnswers,
    }));

    // Auto-save to backend if assessment exists
    if (assessmentId) {
      try {
        const answerArray = Object.entries(newAnswers).map(([questionId, answer]) => ({
          questionId,
          answer,
        }));

        if (answerArray.length > 0) {
          // Show saving indicator
          setAutoSaveStatus('saving');

          // Clear any existing timeout
          if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
          }

          // Auto-save in background (don't block UI)
          apiService
            .submitCareerOrientationAnswers(assessmentId, answerArray)
            .then(() => {
              setAutoSaveStatus('saved');
              // Reset to idle after 2 seconds
              autoSaveTimeoutRef.current = setTimeout(() => {
                setAutoSaveStatus('idle');
              }, 2000);
            })
            .catch(error => {
              console.error('[CareerOrientation] Auto-save failed:', error);
              setAutoSaveStatus('idle');
              // Don't show error toast for auto-save failures to avoid annoying user
            });
        }
      } catch (error) {
        // Silently fail - answers will be saved on complete
        console.error('[CareerOrientation] Auto-save error:', error);
        setAutoSaveStatus('idle');
      }
    }
  };

  const getCurrentGroupAnswers = () => {
    if (!groups[currentGroupIndex]) return {};
    const currentGroup = groups[currentGroupIndex];
    const groupAnswers: Record<string, boolean> = {};
    currentGroup.questions?.forEach(q => {
      if (answers[q.id] !== undefined) {
        groupAnswers[q.id] = answers[q.id];
      }
    });
    return groupAnswers;
  };

  const checkGroupComplete = (groupIndex: number) => {
    if (!groups[groupIndex]) return false;
    const group = groups[groupIndex];
    return group.questions?.every(q => answers[q.id] !== undefined) || false;
  };

  const handleNextGroup = () => {
    if (currentGroupIndex < groups.length - 1) {
      // Track progress through CAT assessment (group completion)
      const currentGroup = groups[currentGroupIndex];
      trackCATProgress({
        group_index: currentGroupIndex + 1,
        group_name: currentGroup?.name || `Group ${currentGroupIndex + 1}`,
        total_groups: groups.length,
      });

      setCurrentGroupIndex(prev => prev + 1);
    } else {
      handleCompleteAssessment();
    }
  };

  const handlePreviousGroup = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(prev => prev - 1);
    }
  };

  const handleCompleteAssessment = async () => {
    if (!assessmentId) {
      toast.error('Không tìm thấy phiên làm bài. Vui lòng thử lại.');
      updateScreen('intro');
      return;
    }

    try {
      setIsSubmitting(true);
      setLoadingMessage('Đang gửi câu trả lời...');

      const allAnswers = groups.flatMap(
        group =>
          group.questions?.map(q => ({
            questionId: q.id,
            answer: answers[q.id] || false,
          })) || []
      );

      // Attempt to submit answers if there are any
      if (allAnswers.length > 0) {
        try {
          await apiService.submitCareerOrientationAnswers(assessmentId, allAnswers);
        } catch (error: any) {
          // If 409, it means assessment might be completed or answers somehow locked.
          // We continue to try completing/fetching results.
          if (error?.response?.status !== 409) {
            console.warn('[CareerOrientation] Submit answers warning:', error);
            // We don't throw here to attempt completion anyway,
            // but in strict mode we might want to.
            // For now, assume if submit fails, we try to see if we can get results.
          }
        }
      }

      setLoadingMessage('Đang phân tích kết quả...');

      let result;
      try {
        const completion = await apiService.completeCareerOrientationAssessment(assessmentId);
        result = completion.result;
      } catch (error: any) {
        // If completion fails (e.g. already completed 409), try fetching current results
        console.warn(
          '[CareerOrientation] Complete assessment failed, trying to fetch existing results:',
          error
        );

        // Fallback: fetch the assessment result directly
        const existingResult = await apiService.getCareerOrientationAssessmentResult(assessmentId);
        // Transform to match the structure expected below if needed,
        // usually getCareerOrientationAssessmentResult returns the same shape as completion.result
        // based on loadFreeResult usage: result.groupResults, result.scores
        result = existingResult;
      }

      if (!result) {
        throw new Error('Không thể lấy kết quả đánh giá.');
      }

      // Validate result structure
      if (!result.scores || !Array.isArray(result.scores)) {
        console.error('[CareerOrientation] Invalid result structure - missing scores:', result);
        throw new Error('Kết quả đánh giá không hợp lệ.');
      }

      if (!result.groupResults || !Array.isArray(result.groupResults)) {
        console.error(
          '[CareerOrientation] Invalid result structure - missing groupResults:',
          result
        );
        throw new Error('Kết quả đánh giá không hợp lệ.');
      }

      setLoadingMessage('Đang tổng hợp điểm số...');

      // Extract teaser titles from top ranked group for CTA display
      // Sort by rank to ensure we get the #1 group
      const sortedResults = [...result.groupResults].sort(
        (a: any, b: any) => (a.rank || 0) - (b.rank || 0)
      );
      const topGroupResult = sortedResults[0];
      const groupWithTeaser = topGroupResult?.group as any;
      if (groupWithTeaser?.fullDescTeaserTitles) {
        setTopGroupTeaserTitles(groupWithTeaser.fullDescTeaserTitles);
      } else {
        setTopGroupTeaserTitles([]);
      }

      const groupScores = result.scores.map(score => ({
        id: score.groupId,
        score: score.score,
        name: score.group?.name || 'Unknown',
      }));

      groupScores.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        const groupA = groups.find(g => g.id === a.id);
        const groupB = groups.find(g => g.id === b.id);
        return (groupA?.order || 0) - (groupB?.order || 0);
      });
      setScores(groupScores);

      // Top groups: Sort by score DESC, then by group order ASC (tiebreaker)
      const allGroupScores = result.scores.map(score => ({
        id: score.groupId,
        score: score.score,
        name: score.group?.name || 'Unknown',
        order: score.group?.order || 0,
      }));

      // Sort: Higher score first, then lower order number (for ties)
      allGroupScores.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score; // Descending score
        }
        return a.order - b.order; // Ascending order (tiebreaker)
      });

      const top3 = allGroupScores.slice(0, 3);
      setTopGroups(top3);

      // Track CAT completion event
      const totalQuestions = groups.reduce((sum, g) => sum + (g.questions?.length || 0), 0);
      trackCATComplete({ total_questions: totalQuestions });

      setLoadingMessage('Hoàn thành! Đang chuyển đến kết quả...');

      // Small delay to show completion message
      await new Promise(resolve => setTimeout(resolve, 500));

      setIsSubmitting(false);
      setLoadingMessage('');

      // Track viewing free result
      trackViewFreeResult({
        top_groups: top3.map(g => g.name),
        session_id: assessmentId,
      });

      // Always navigate to results screen
      updateScreen('free-result', assessmentId);
    } catch (error: any) {
      console.error('[CareerOrientation] Failed to complete assessment:', error);
      setIsSubmitting(false);
      setLoadingMessage('');
      toast.error('Không thể hoàn thành đánh giá. Vui lòng thử lại.');
      // Even on error, try to navigate to intro instead of leaving user stuck
      updateScreen('intro');
    }
  };

  const handleStart = async () => {
    // Check if user is authenticated
    if (!user || !localStorage.getItem('accessToken')) {
      toast.error('Vui lòng đăng nhập để sử dụng công cụ định hướng nghề nghiệp');
      navigate('/login?redirect=/huong-nghiep-cat?screen=intro');
      return;
    }

    // Navigate to instruction screen
    updateScreen('instruction');
  };

  const handleBeginQuestions = async () => {
    // Check if user is authenticated
    if (!user || !localStorage.getItem('accessToken')) {
      toast.error('Vui lòng đăng nhập để sử dụng công cụ định hướng nghề nghiệp');
      navigate('/login?redirect=/huong-nghiep-cat?screen=instruction');
      return;
    }

    // Require level before starting questions; default to THPT if user came from intro without selection
    const levelToUse = selectedLevel || 'THPT';
    if (!selectedLevel) {
      setSelectedLevel(levelToUse);
    }

    // If session already exists, resume it
    if (assessmentId) {
      await loadAssessmentQuestions(assessmentId);
      updateScreen('questions', assessmentId);
      return;
    }

    try {
      const assessment = await apiService.createCareerOrientationAssessment(levelToUse);
      setAssessmentId(assessment.id);
      await loadAssessmentQuestions(assessment.id);

      // Track CAT start event for conversion funnel
      trackCATStart({ education_level: levelToUse });

      updateScreen('questions', assessment.id);
    } catch (error: any) {
      console.error('[CareerOrientation] Failed to start assessment:', error);
      if (error?.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login?redirect=/huong-nghiep-cat?screen=instruction');
      } else {
        const errorMessage = error?.message || 'Không thể bắt đầu đánh giá. Vui lòng thử lại.';
        setGroupsLoadError(errorMessage);
        toast.error(errorMessage);
      }
    }
  };

  const handleSubmit = () => {
    if (!checkGroupComplete(currentGroupIndex)) {
      return;
    }
    handleNextGroup();
  };

  const _handleSelectLevel = async (level: 'THCS' | 'THPT') => {
    // Check if user is authenticated
    if (!user || !localStorage.getItem('accessToken')) {
      toast.error('Vui lòng đăng nhập để sử dụng công cụ định hướng nghề nghiệp');
      navigate('/login?redirect=/huong-nghiep-cat?screen=intro');
      return;
    }

    try {
      setSelectedLevel(level);
      await loadGroups();
      const assessment = await apiService.createCareerOrientationAssessment(level);
      setAssessmentId(assessment.id);
      updateScreen('instruction', assessment.id);
    } catch (error: any) {
      console.error('Failed to select level:', error);
      if (error?.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login?redirect=/huong-nghiep-cat?screen=intro');
      } else {
        toast.error(error?.message || 'Không thể tạo đánh giá. Vui lòng thử lại.');
      }
    }
  };

  const handleSelectLevelForPayment = async (level: 'THCS' | 'THPT') => {
    try {
      setSelectedLevel(level);
      if (assessmentId) {
        await apiService.updateCareerOrientationEducationLevel(assessmentId, level);
      }
      updateScreen('payment', assessmentId);
      setIsPaymentDialogOpen(true);
    } catch (error: any) {
      console.error('Failed to update education level:', error);
      toast.error('Không thể cập nhật cấp học. Vui lòng thử lại.');
    }
  };

  const handleViewFullReport = async () => {
    if (!selectedLevel) {
      updateScreen('payment', assessmentId);
      return;
    }

    if (assessmentId && selectedLevel) {
      try {
        await apiService.updateCareerOrientationEducationLevel(assessmentId, selectedLevel);
      } catch (error: any) {
        console.error('Failed to update education level:', error);
        toast.error('Không thể cập nhật cấp học. Vui lòng thử lại.');
        return;
      }
    }

    // Track begin checkout event for conversion funnel
    trackBeginCheckout({
      value: catReportPrice,
      currency: 'VND',
      session_id: assessmentId || undefined,
    });

    updateScreen('payment', assessmentId);

    // Don't open payment dialog if bypass is active - let useEffect handle it
    if (!isTestBypassActive) {
      setIsPaymentDialogOpen(true);
    }
  };

  const handlePaymentSuccess = useCallback(
    async (transactionId?: string) => {
      // Prevent duplicate calls - use transactionId as key
      const callKey = `${assessmentId}-${transactionId || 'no-tx'}`;
      if (paymentSuccessProcessedRef.current === callKey) {
        console.log('Payment success already processed, skipping duplicate call');
        return;
      }
      paymentSuccessProcessedRef.current = callKey;

      setIsPaymentDialogOpen(false);

      // Start loading sequence
      setIsSubmitting(true);
      setLoadingMessage('Đang xác thực thanh toán...');
      // Artificial delay for better UX
      await new Promise(resolve => setTimeout(resolve, 800));

      if (!assessmentId) {
        setIsSubmitting(false);
        setLoadingMessage('');
        updateScreen('full-report');
        return;
      }

      // Link payment to assessment and grant full report access
      // In mock mode (transactionId starts with "CAT-MOCK-" or "VSAT-MOCK-"), grant access directly
      if (transactionId) {
        // Always attempt link payment first to create transaction record
        try {
          await apiService.linkPaymentToCareerOrientationAssessment(assessmentId, transactionId);
          setLoadingMessage('Thanh toán thành công! Đang mở khóa báo cáo...');
        } catch (error: any) {
          // Suppress error for Mock transactions as they won't exist on server
          // Check if this is a mock payment (CAT or VSAT system)
          if (transactionId.startsWith('CAT-MOCK-') || transactionId.startsWith('VSAT-MOCK-')) {
            console.warn('Mock payment link skipped (expected)', error.message);
          } else {
            console.error('Failed to link payment to assessment:', error);
          }

          // Fallback for Mock Mode if backend rejects fake transaction ID
          let fallbackSuccess = false;
          // Mock payment - grant access immediately for testing
          if (transactionId.startsWith('CAT-MOCK-') || transactionId.startsWith('VSAT-MOCK-')) {
            try {
              console.log('Fallback to direct access grant for mock mode');
              await apiService.grantCareerOrientationFullReportAccess(assessmentId);
              toast.success(
                'Đã kích hoạt chế độ giả lập (Giao dịch có thể không được lưu trên hệ thống).'
              );
              setLoadingMessage('Đang mở khóa báo cáo (Chế độ giả lập)...');
              fallbackSuccess = true;
            } catch (grantError) {
              console.error('Failed to grant access fallback:', grantError);
            }
          }

          if (!fallbackSuccess) {
            setIsSubmitting(false);
            setLoadingMessage('');
            toast.error('Không thể liên kết thanh toán. Vui lòng thử lại.');
            paymentSuccessProcessedRef.current = null; // Reset on error
            return;
          }
        }
      }

      // Load full report data after payment success
      try {
        setLoadingMessage('Đang tải dữ liệu phân tích chuyên sâu...');

        // Fix: Ensure we set loading state immediately and don't rely on finally block which might run early
        setLoadingFullReport(true);

        // Artificial delay for better UX
        await new Promise(resolve => setTimeout(resolve, 1000));

        const fullReport = await apiService.getCareerOrientationFullReport(assessmentId || '');

        setLoadingMessage('Đang hoàn tất báo cáo cá nhân hóa...');

        // FIX: Match loadFullReportForSession behavior - get ALL groups from scores
        // This ensures compass shows all 6 Holland groups with complete data
        const allGroupsFromScores = (fullReport.scores || []).map((score: any) => ({
          ...score.group,
          questions: score.group?.questions || [],
        })) as CareerOrientationGroupWithQuestions[];

        // Sort by group order to maintain consistent display
        const sortedAllGroups = allGroupsFromScores.sort(
          (a: any, b: any) => (a.order || 0) - (b.order || 0)
        );

        // Set groups state (important for buildFullReportViewModel)
        setGroups(sortedAllGroups);

        // FIX: Also update scores from fullReport to ensure consistency
        // The scores array is used in buildFullReportViewModel
        if (fullReport.scores && fullReport.scores.length > 0) {
          const newScores = fullReport.scores.map((s: any) => ({
            id: s.groupId,
            score: s.score,
            name: s.group?.name || '',
          }));
          setScores(newScores);
        }

        // FIX: Update topGroups sorted by score DESC then order ASC (match loadFullReportForSession)
        if (fullReport.scores && fullReport.scores.length > 0) {
          const allGroupScores = fullReport.scores.map((score: any) => ({
            id: score.groupId,
            score: score.score,
            name: score.group?.name || '',
            order: score.group?.order || 0,
          }));

          allGroupScores.sort((a: any, b: any) => {
            if (b.score !== a.score) {
              return b.score - a.score; // Descending score
            }
            return a.order - b.order; // Ascending order (tiebreaker)
          });

          const computedTopGroups = allGroupScores.slice(0, 3);
          setTopGroups(computedTopGroups);
        }

        // Set education level if available
        if (fullReport.educationLevel) {
          setSelectedLevel(fullReport.educationLevel);
        }

        // Update fullReportData with sorted all groups (same as loadFullReportForSession)
        setFullReportData(sortedAllGroups);

        // Final delay
        await new Promise(resolve => setTimeout(resolve, 500));

        setIsSubmitting(false);

        // Update screen only AFTER data is set and we are ready
        await updateScreen('full-report', assessmentId);

        // Set loading to false AFTER screen update to prevent "empty" flash
        setLoadingFullReport(false);

        // Track viewing full report
        trackViewFullReport(assessmentId);
      } catch (error: any) {
        console.error('Failed to load full report:', error);
        setIsSubmitting(false);
        setLoadingMessage('');
        setLoadingFullReport(false); // Valid to stop loading on error

        // Do NOT navigate to full-report on error, stay on current screen so user can retry
        toast.error('Không thể tải dữ liệu báo cáo. Vui lòng thử lại hoặc liên hệ hỗ trợ.');
      }
    },
    [assessmentId, updateScreen]
  );

  const handleRestart = () => {
    setCurrentGroupIndex(0);
    setScores([]);
    setTopGroups([]);
    setSelectedLevel(null);
    setAssessmentId(null);
    setFullReportData(null);
    updateScreen('intro');
  };

  const handleSendEmail = async (email: string) => {
    if (!assessmentId) {
      toast.error('Không tìm thấy mã đánh giá để gửi email.');
      return;
    }

    // Close modal immediately for better UX
    setIsEmailModalOpen(false);

    // Show preparing toast
    const toastId = toast.loading('Đang gửi email...', {
      position: 'bottom-right',
    });

    try {
      // Call backend to send email (backend handles PDF generation)
      await apiService.sendCareerOrientationReportEmail(assessmentId, email);

      // Show success toast from bottom
      toast.success(`Đã gửi báo cáo đến ${email}`, {
        id: toastId,
        position: 'bottom-right',
        duration: 4000,
      });
    } catch (error: any) {
      console.error('Failed to send email:', error);
      toast.error(error.message || 'Không thể gửi email. Vui lòng thử lại sau.', {
        id: toastId,
        position: 'bottom-right',
        duration: 5000,
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Đang tạo file PDF...');

      const { buildFullReportViewModel } = await import(
        '../components/career-orientation/full-report/useFullReportViewModel'
      );

      // Get user info
      const userName =
        userProfile?.name || user?.name || user?.firstName + ' ' + user?.lastName || 'Học sinh';
      const userClass =
        userProfile?.class && userProfile.class !== 'N/A'
          ? userProfile.class
          : selectedLevel === 'THPT'
            ? 'Định hướng ngành đại học'
            : selectedLevel === 'THCS'
              ? 'Định hướng học nghề'
              : 'N/A';
      const testDate = new Date().toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const assessmentIdDisplay = assessmentId
        ? `#CAT-${new Date().getFullYear()}-${assessmentId.slice(-4)}`
        : `#CAT-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`;

      // Build view model data
      const { top3Codes, top3Scores, allGroupsWithPositions } = buildFullReportViewModel({
        scores,
        groups,
        topGroups,
        fullReportData,
      });

      // Map normalized scores for PDF
      const normalizedTop3Scores = top3Scores.map(s => ({
        score: (s as any).actualScore ?? (s as any).score ?? 0,
      }));

      const allScoresForPDF = allGroupsWithPositions.map(g => ({
        id: g.id,
        name: g.name,
        actualScore: g.score?.actualScore ?? 0,
        maxScore: g.score?.maxScore ?? 8,
      }));

      // Call backend API to generate PDF
      const requestData = {
        userName,
        userClass,
        testDate,
        assessmentId: assessmentIdDisplay,
        top3Codes,
        top3Scores: normalizedTop3Scores,
        allScores: allScoresForPDF,
        educationLevel: selectedLevel || 'THPT',
      };

      const response = await apiService.generateCareerOrientationPDF(requestData);

      // Download the PDF blob
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Bao-cao-huong-nghiep_${userName.replace(/[^a-zA-Z0-9]/g, '_')}_${assessmentIdDisplay}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss(loadingToast);
      toast.success('Đã tải xuống báo cáo PDF thành công!');
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      toast.error('Không thể xuất báo cáo PDF. Vui lòng thử lại sau.');
    }
  };

  const seoElements = (
    <>
      <SEOHead
        title='Trắc nghiệm hướng nghiệp CAT | Đánh giá năng lực bản quyền IEQTE'
        description='Trắc nghiệm hướng nghiệp CAT - Công cụ đánh giá năng lực và định hướng nghề nghiệp cho học sinh THPT. Phương pháp khoa học Holland RIASEC kết hợp ĐGNL bản quyền IEQTE.'
        canonicalUrl={getCanonicalUrl('/huong-nghiep-cat')}
        keywords='trắc nghiệm hướng nghiệp, CAT, Holland RIASEC, đánh giá năng lực, IEQTE, định hướng nghề nghiệp, THPT'
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Hướng nghiệp CAT', url: getCanonicalUrl('/huong-nghiep-cat') },
          ],
        }}
        id='career'
      />
    </>
  );

  // Show loading state while loading groups (only when actually loading)
  if (loadingGroups) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        {seoElements}
        <div className='text-center space-y-4'>
          <Loader2 className='h-8 w-8 animate-spin text-primary mx-auto' />
          <p className='text-muted-foreground'>Đang tải câu hỏi...</p>
        </div>
      </div>
    );
  }

  // FULL REPORT SCREEN (Executive Summary Dashboard) - new layout using ReportLayout
  if (currentScreen === 'full-report') {
    const userName =
      userProfile?.name || user?.name || user?.firstName + ' ' + user?.lastName || 'Học sinh';
    const userClass = userProfile?.class || 'N/A';
    const testDate = new Date().toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const assessmentIdDisplay = assessmentId
      ? `#CAT-${new Date().getFullYear()}-${assessmentId.slice(-4)}`
      : `#CAT-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000)}`;

    if (loadingFullReport) {
      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
          <div className='text-center space-y-4'>
            <Loader2 className='h-8 w-8 animate-spin text-primary mx-auto' />
            <p className='text-gray-500'>Đang tải báo cáo kết quả...</p>
          </div>
        </div>
      );
    }

    // FIX: Wait for fullReportData to be populated before rendering
    // This prevents race condition where screen updates before state is committed
    if (!fullReportData || fullReportData.length === 0) {
      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
          <div className='text-center space-y-4'>
            <Loader2 className='h-8 w-8 animate-spin text-primary mx-auto' />
            <p className='text-gray-500'>Đang tải dữ liệu báo cáo chi tiết...</p>
          </div>
        </div>
      );
    }

    if (!scores || scores.length === 0 || !groups || groups.length === 0) {
      return (
        <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
          <div className='text-center space-y-4'>
            <AlertCircle className='h-8 w-8 text-red-500 mx-auto' />
            <p className='text-red-600 font-semibold'>Không có dữ liệu đánh giá</p>
            <Button onClick={() => updateScreen('intro')} variant='outline'>
              Quay lại
            </Button>
          </div>
        </div>
      );
    }

    const { displayGroups, top3Codes, top3Scores, remainingScores, allGroupsWithPositions } =
      buildFullReportViewModel({
        scores,
        groups,
        topGroups,
        fullReportData,
      });

    const remaining = allGroupsWithPositions
      .filter(g => !g.isTop3)
      .map(g => ({
        id: g.id,
        score: g.score.actualScore ?? g.score.score,
        name: g.name,
        shortName: g.shortName,
        code: g.code,
        color: g.color,
        shortDesc: g.shortDesc,
      }));

    const normalizedTop3Scores = top3Scores.map(s => ({
      score: (s as any).actualScore ?? (s as any).score ?? 0,
    }));

    return (
      <div data-report-card>
        {seoElements}
        <SendEmailModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSend={handleSendEmail}
          defaultEmail={user?.email || ''}
        />
        <ReportLayout
          onSendEmail={() => setIsEmailModalOpen(true)}
          userName={userName}
          userClass={userClass}
          testDate={testDate}
          assessmentIdDisplay={assessmentIdDisplay}
          userProfile={userProfile}
          onRestart={handleRestart}
          onDownloadPdf={handleExportPDF}
          top3Codes={top3Codes}
          top3Scores={normalizedTop3Scores}
          remaining={remaining}
          displayGroups={displayGroups}
          compassGroups={allGroupsWithPositions}
          selectedLevel={selectedLevel || 'THPT'}
        />
      </div>
    );
  }

  // Show error only if there was an actual error loading groups
  // AND we're on a screen that requires groups (questions, free-result, full-report)
  if (groupsLoadError && currentScreen === 'questions') {
    return (
      <div className='min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 flex items-center justify-center px-4 pt-20 pb-12'>
          <div className='max-w-md w-full'>
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 text-center'>
              {/* Error Icon with gradient background */}
              <div className='mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 flex items-center justify-center mb-6'>
                <AlertCircle className='h-10 w-10 text-red-500 dark:text-red-400' />
              </div>

              {/* Error Title */}
              <h2 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                Không thể tải câu hỏi
              </h2>

              {/* Error Message */}
              <p className='text-gray-500 dark:text-gray-400 text-sm mb-6 leading-relaxed'>
                Phiên làm bài không tồn tại hoặc đã hết hạn. Vui lòng bắt đầu một bài đánh giá mới.
              </p>

              {/* Technical Details (collapsible look) */}
              <div className='bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 mb-6'>
                <p className='text-xs text-gray-400 dark:text-gray-500 font-mono break-all'>
                  {groupsLoadError}
                </p>
              </div>

              {/* Action Buttons */}
              <div className='flex flex-col sm:flex-row gap-3'>
                <Button
                  onClick={() => {
                    setGroupsLoadError(null);
                    setAssessmentId(null);
                    updateScreen('intro');
                  }}
                  className='flex-1 bg-primary hover:bg-primary/90'
                >
                  <RotateCcw className='h-4 w-4 mr-2' />
                  Bắt đầu lại
                </Button>
                <Button onClick={() => navigate('/')} variant='outline' className='flex-1'>
                  Về trang chủ
                </Button>
              </div>
            </div>

            {/* Help text */}
            <p className='text-center text-xs text-gray-400 dark:text-gray-500 mt-4'>
              Nếu lỗi tiếp tục xảy ra, vui lòng liên hệ hỗ trợ
            </p>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // Show error if on questions screen but no groups loaded (shouldn't happen, but safety check)
  if (currentScreen === 'questions' && groups.length === 0 && !loadingGroups) {
    return (
      <div className='min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex flex-col'>
        <UnifiedHeader />
        <main className='flex-1 flex items-center justify-center px-4 pt-20 pb-12'>
          <div className='max-w-md w-full'>
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 text-center'>
              {/* Warning Icon */}
              <div className='mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 flex items-center justify-center mb-6'>
                <AlertCircle className='h-10 w-10 text-amber-500 dark:text-amber-400' />
              </div>

              {/* Title */}
              <h2 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                Không có dữ liệu câu hỏi
              </h2>

              {/* Message */}
              <p className='text-gray-500 dark:text-gray-400 text-sm mb-6 leading-relaxed'>
                Hệ thống chưa tải được danh sách câu hỏi. Vui lòng thử lại hoặc bắt đầu bài đánh giá
                mới.
              </p>

              {/* Action Buttons */}
              <div className='flex flex-col sm:flex-row gap-3'>
                <Button
                  onClick={() => {
                    setAssessmentId(null);
                    updateScreen('intro');
                  }}
                  className='flex-1 bg-primary hover:bg-primary/90'
                >
                  <RotateCcw className='h-4 w-4 mr-2' />
                  Bắt đầu lại
                </Button>
                <Button onClick={() => navigate('/')} variant='outline' className='flex-1'>
                  Về trang chủ
                </Button>
              </div>
            </div>
          </div>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // Calculate progress
  const currentGroup = groups[currentGroupIndex];
  const progress =
    currentGroup && currentGroup.questions
      ? (currentGroup.questions.filter(q => answers[q.id] !== undefined).length /
          currentGroup.questions.length) *
        100
      : 0;
  const isCurrentGroupComplete = checkGroupComplete(currentGroupIndex);

  // INTRO SCREEN - New Design from Landing Page HTML
  if (currentScreen === 'intro') {
    return (
      <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
        {seoElements}
        <UnifiedHeader />
        <main className='pt-14 pb-12 flex-1'>
          {/* Hero Section (lines 72-133) */}
          <section className='relative overflow-hidden bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-background-dark pt-16 pb-20 lg:pt-24 lg:pb-28'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10'>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
                <div className='text-center lg:text-left'>
                  <span className='inline-block py-1 px-3 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs font-bold tracking-wide uppercase mb-4'>
                    Hướng nghiệp CAT - Career Aptitude Test
                  </span>
                  <h1 className='text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-6 leading-tight'>
                    HIỂU RÕ BẢN THÂN
                    <br />
                    <span className='text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-800 dark:from-yellow-400 dark:to-yellow-200'>
                      TRƯỚC KHI CHỌN NGÀNH
                    </span>
                  </h1>
                  <p className='mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-lg mx-auto lg:mx-0 mb-8'>
                    Công cụ trắc nghiệm hướng nghiệp CAT đã{' '}
                    <span className='font-bold'>đăng ký bản quyền</span>, thử nghiệm trên{' '}
                    <span className='font-bold'>diện rộng tại Việt Nam</span>. Giúp bạn khám phá
                    điểm mạnh, tính cách và định hướng lộ trình nghề nghiệp vững chắc.
                  </p>
                  <div className='flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-4'>
                    <button
                      onClick={handleStart}
                      className='w-full sm:w-auto px-8 py-3.5 rounded-lg bg-primary hover:bg-primary-hover text-white text-base font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-center uppercase'
                    >
                      LÀM BÀI TEST NGAY - MIỄN PHÍ
                    </button>
                    <Link
                      to='/huong-nghiep-cat?screen=instruction'
                      className='text-sm font-medium text-gray-600 hover:text-primary underline decoration-2 decoration-primary/30 dark:text-gray-400 dark:hover:text-primary transition-colors'
                    >
                      Xem mẫu báo cáo
                    </Link>
                  </div>
                </div>
                <div className='relative mx-auto w-full max-w-lg lg:max-w-none'>
                  <div className='relative rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700'>
                    <div className='absolute top-0 left-0 w-full h-8 bg-gray-100 dark:bg-gray-700 flex items-center px-4 space-x-2 border-b border-gray-200 dark:border-gray-600'>
                      <div className='w-3 h-3 rounded-full bg-red-400'></div>
                      <div className='w-3 h-3 rounded-full bg-yellow-400'></div>
                      <div className='w-3 h-3 rounded-full bg-green-400'></div>
                    </div>
                    <div className='pt-8 pb-0 px-0'>
                      <img
                        alt='Dashboard analytics illustration'
                        className='w-full h-auto object-cover opacity-90 dark:opacity-80'
                        src='https://lh3.googleusercontent.com/aida-public/AB6AXuCTSn0_HcHZb59cnucaf9w-jhbNG6RYCFPxQgjsJgrpSI3--awJnvJyqNATuk4f-P-_m9U6SqJyL-YxXmRhsqWvTZFR3nnpc25cZHg53bWcNKo13YrniGuCdpz1JmmzobGTKIpvtS0ScWEkKPjlLTB31qm-4PzEW1Bgi2UDvy2tUNV67pK7uWJsDcYF2u9dGLFRQuKtOgp0HjXF2FCEtsgIg1Jw7-oacA2C_V7chDYqWA4Oa6fQkHolwVtBdc23znbosbciDualGgY'
                      />
                      <div className='absolute inset-0 top-8 bg-surface-light dark:bg-surface-dark p-6 grid grid-cols-2 gap-4 overflow-hidden'>
                        <div className='col-span-1 p-4 bg-yellow-50 dark:bg-gray-700 rounded-lg shadow-sm'>
                          <div className='h-32 w-full bg-yellow-100 dark:bg-gray-600 rounded-full flex items-center justify-center'>
                            <svg
                              className='w-24 h-24 text-yellow-500 dark:text-yellow-400 fill-current opacity-50'
                              viewBox='0 0 100 100'
                            >
                              <polygon points='50,10 90,40 80,80 20,80 10,40'></polygon>
                            </svg>
                          </div>
                          <div className='mt-2 h-2 w-16 bg-gray-200 dark:bg-gray-500 rounded mx-auto'></div>
                        </div>
                        <div className='col-span-1 space-y-2'>
                          <div className='h-8 bg-gray-100 dark:bg-gray-700 rounded w-full'></div>
                          <div className='h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded w-3/4'></div>
                          <div className='h-8 bg-blue-100 dark:bg-blue-900/30 rounded w-5/6'></div>
                        </div>
                        <div className='col-span-2 mt-4'>
                          <div className='flex items-end space-x-4 h-32 justify-center'>
                            <div className='w-8 bg-blue-400 dark:bg-blue-600 rounded-t h-16'></div>
                            <div className='w-8 bg-yellow-400 dark:bg-yellow-600 rounded-t h-24'></div>
                            <div className='w-8 bg-green-400 dark:bg-green-600 rounded-t h-20'></div>
                            <div className='w-8 bg-purple-400 dark:bg-purple-600 rounded-t h-28'></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='absolute -bottom-10 -left-10 w-48 h-48 lg:w-64 lg:h-64 z-20 hidden sm:block'>
                    <img
                      alt='Student studying'
                      className='w-full h-full object-cover rounded-full border-8 border-white dark:border-gray-800 shadow-xl'
                      src='/students-studying.png'
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CAT DÀNH CHO AI? Section (lines 134-170) */}
          <section className='py-20 bg-surface-light dark:bg-surface-dark'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
              <div className='text-center mb-16'>
                <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                  HƯỚNG NGHIỆP CAT DÀNH CHO AI?
                </h2>
                <div className='w-20 h-1 bg-primary mx-auto rounded-full'></div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                <div className='bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 transition-all duration-300 group'>
                  <div className='w-14 h-14 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                    <span className='material-icons-outlined text-3xl text-blue-600 dark:text-blue-400'>
                      school
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                    Học sinh THPT
                  </h3>
                  <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                    Bài kiểm tra năng lực toàn diện kết hợp <strong>trắc nghiệm tâm lý</strong>,
                    giúp học sinh nhận diện điểm mạnh và thiên hướng nghề nghiệp.
                  </p>
                </div>
                <div className='bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 transition-all duration-300 group'>
                  <div className='w-14 h-14 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                    <span className='material-icons-outlined text-3xl text-yellow-600 dark:text-yellow-400'>
                      family_restroom
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                    Phụ huynh
                  </h3>
                  <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                    <strong>Thấu hiểu và đồng hành</strong> cùng con trong giai đoạn chọn ngành quan
                    trọng, dựa trên các dữ liệu đánh giá khoa học và khách quan.
                  </p>
                </div>
                <div className='bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-gray-700 transition-all duration-300 group'>
                  <div className='w-14 h-14 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300'>
                    <span className='material-icons-outlined text-3xl text-green-600 dark:text-green-400'>
                      work_outline
                    </span>
                  </div>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                    Cố vấn nghề nghiệp
                  </h3>
                  <p className='text-gray-600 dark:text-gray-300 text-sm leading-relaxed'>
                    Công cụ để chuyên gia tư vấn nghề nghiệp có cái nhìn tổng quan định hướng việc
                    làm cho học sinh.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CAT HOẠT ĐỘNG NHƯ THẾ NÀO? Section (lines 171-210) */}
          <section className='py-20 bg-yellow-50 dark:bg-gray-900'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
              <div className='text-center mb-16'>
                <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                  HƯỚNG NGHIỆP CAT HOẠT ĐỘNG NHƯ THẾ NÀO?
                </h2>
                <div className='w-20 h-1 bg-primary mx-auto rounded-full'></div>
              </div>
              <div className='relative'>
                <div className='hidden md:block absolute top-10 left-0 w-full h-0.5 bg-gray-300 dark:bg-gray-700 -z-0'></div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-10'>
                  <div className='relative z-10 flex flex-col items-center text-center'>
                    <div className='w-20 h-20 rounded-full bg-white dark:bg-gray-800 border-4 border-yellow-100 dark:border-yellow-900 flex items-center justify-center shadow-lg mb-6'>
                      <span className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                        1.
                      </span>
                    </div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-3'>
                      Thực hiện bài test trực tuyến
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xs'>
                      Hoàn thành bộ câu hỏi trắc nghiệm được thiết kế tối ưu hóa thời gian, thao tác
                      đơn giản trên mọi thiết bị.
                    </p>
                  </div>
                  <div className='relative z-10 flex flex-col items-center text-center'>
                    <div className='w-20 h-20 rounded-full bg-white dark:bg-gray-800 border-4 border-yellow-100 dark:border-yellow-900 flex items-center justify-center shadow-lg mb-6'>
                      <span className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                        2.
                      </span>
                    </div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-3'>
                      Hệ thống phân tích tự động
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xs'>
                      Ứng dụng các thuật toán AI thông minh để xử lý dữ liệu, đánh giá sâu chuỗi sở
                      thích và năng lực của bạn.
                    </p>
                  </div>
                  <div className='relative z-10 flex flex-col items-center text-center'>
                    <div className='w-20 h-20 rounded-full bg-white dark:bg-gray-800 border-4 border-yellow-100 dark:border-yellow-900 flex items-center justify-center shadow-lg mb-6'>
                      <span className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
                        3.
                      </span>
                    </div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-3'>
                      Nhận kết quả &amp; Gợi ý ngành
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400 max-w-xs'>
                      Nhận báo cáo chi tiết và danh sách ngành nghề phù hợp nhất gửi trực tiếp về
                      email của bạn ngay lập tức.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* BẠN NHẬN ĐƯỢC GÌ TỪ CAT Section (lines 211-310) */}
          <section className='py-20 bg-surface-light dark:bg-surface-dark'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
              <div className='text-center mb-16'>
                <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
                  BẠN NHẬN ĐƯỢC GÌ TỪ HƯỚNG NGHIỆP CAT
                </h2>
                <div className='w-20 h-1 bg-primary mx-auto rounded-full'></div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12'>
                <div className='flex items-start space-x-4'>
                  <div className='flex-shrink-0'>
                    <div className='w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400'>
                      <span className='material-icons-outlined'>psychology</span>
                    </div>
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                      Hiểu sâu về năng lực cốt lõi
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Hiểu sâu về năng lực cốt lõi, trắc nghiệm xác nhận tiềm năng và hiểu về năng
                      lực cốt lõi.
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-4'>
                  <div className='flex-shrink-0'>
                    <div className='w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center text-yellow-600 dark:text-yellow-400'>
                      <span className='material-icons-outlined'>lightbulb</span>
                    </div>
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                      Gợi ý ngành nghề phù hợp
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Gợi ý ngành nghề phù hợp, gợi ý ngành nghề mà công cụ phụ hiện công minh.
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-4'>
                  <div className='flex-shrink-0'>
                    <div className='w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400'>
                      <span className='material-icons-outlined'>trending_up</span>
                    </div>
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                      Kế hoạch phát triển cá nhân
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Xây dựng lộ trình phát triển cá nhân dựa trên năng lực và mục tiêu nghề nghiệp
                      của bạn.
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-4'>
                  <div className='flex-shrink-0'>
                    <div className='w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400'>
                      <span className='material-icons-outlined'>groups</span>
                    </div>
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                      Kho dữ liệu ngành nghề đa dạng
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Cung cấp thông tin chi tiết, yêu cầu và cơ hội việc làm của hàng ngàn ngành
                      nghề hiện đại.
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-4'>
                  <div className='flex-shrink-0'>
                    <div className='w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400'>
                      <span className='material-icons-outlined'>school</span>
                    </div>
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                      Định hướng giáo dục tiếp theo
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Gợi ý các chương trình đào tạo, trường đại học phù hợp với năng lực của bạn.
                    </p>
                  </div>
                </div>
                <div className='flex items-start space-x-4'>
                  <div className='flex-shrink-0'>
                    <div className='w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400'>
                      <span className='material-icons-outlined'>assignment_turned_in</span>
                    </div>
                  </div>
                  <div>
                    <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                      Mục tiêu rõ ràng, động lực vững vàng
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      Tự tin hơn vào con đường nghề nghiệp khi biết rõ bản thân mình.
                    </p>
                  </div>
                </div>
              </div>
              <div className='mt-16 bg-yellow-50 dark:bg-gray-800 rounded-2xl p-8 flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8'>
                <div className='flex-shrink-0'>
                  <div className='w-24 h-24 rounded-full bg-white dark:bg-gray-700 flex items-center justify-center shadow-sm'>
                    <span className='material-icons-outlined text-5xl text-yellow-600 dark:text-yellow-400'>
                      verified_user
                    </span>
                  </div>
                </div>
                <div className='text-center md:text-left'>
                  <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                    BẢN QUYỀN & CHẤT LƯỢNG KHOA HỌC
                  </h3>
                  <p className='text-lg font-medium text-gray-800 dark:text-gray-200 mb-1'>
                    Sản phẩm của Viện Khảo thí và Đánh giá Chất lượng Giáo dục. Đã được Cục Bản
                    quyền tác giả chứng nhận (14/09/2024). Dữ liệu đối sánh chuẩn xác trên 53.000
                    mẫu người dùng.
                  </p>
                  <div className='mt-3 flex items-center justify-center md:justify-start relative group cursor-help w-fit mx-auto md:mx-0'>
                    <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800 z-10 relative'>
                      <span className='material-icons-outlined text-sm mr-1'>verified</span>
                      Đã chứng nhận bản quyền
                    </span>
                    {/* Hover Image Popup */}
                    <div className='absolute left-1/2 -translate-x-1/2 bottom-full mb-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 w-64 md:w-80'>
                      <div className='bg-white dark:bg-gray-800 p-2 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700'>
                        <img
                          src='/giay-chung-nhan-ban-quyen.jpg'
                          alt='Giấy chứng nhận đăng ký quyền tác giả'
                          className='w-full h-auto rounded'
                        />
                        <div className='absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-4 h-4 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700'></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* THPT Exam Practice CTA Section (lines 311-325) */}
          <section className='py-16 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 border-t border-gray-200 dark:border-gray-700'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
              <div className='text-center mb-10'>
                <h3 className='text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2'>
                  LUYỆN THI & ĐÁNH GIÁ NĂNG LỰC TOÀN DIỆN
                </h3>
                <div className='w-24 h-1 bg-primary mx-auto rounded-full'></div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
                {/* Cột 1: Thi thử như thật */}
                <div className='bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow'>
                  <h4 className='text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center'>
                    <span className='material-icons-outlined text-primary mr-2'>timer</span>
                    Thi thử như thật
                  </h4>
                  <ul className='space-y-3'>
                    <li className='flex items-start text-gray-600 dark:text-gray-300'>
                      <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                        check_circle
                      </span>
                      <span>Mô phỏng áp lực thời gian thực tế.</span>
                    </li>
                    <li className='flex items-start text-gray-600 dark:text-gray-300'>
                      <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                        check_circle
                      </span>
                      <span>Nhận diện rõ điểm mạnh - điểm yếu kiến thức.</span>
                    </li>
                    <li className='flex items-start text-gray-600 dark:text-gray-300'>
                      <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                        check_circle
                      </span>
                      <span>Chuẩn bị sẵn sàng cho kỳ thi trên máy tính sắp tới.</span>
                    </li>
                  </ul>
                </div>

                {/* Cột 2: Tại sao cần kết hợp */}
                <div className='bg-white/60 dark:bg-gray-800/60 p-6 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow'>
                  <h4 className='text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center'>
                    <span className='material-icons-outlined text-primary mr-2'>
                      model_training
                    </span>
                    Tại sao cần kết hợp với CAT?
                  </h4>
                  <ul className='space-y-3'>
                    <li className='flex items-start text-gray-600 dark:text-gray-300'>
                      <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                        check_circle
                      </span>
                      <span>
                        Kết hợp <strong className='text-gray-800 dark:text-white'>Sở thích</strong>{' '}
                        (CAT) +{' '}
                        <strong className='text-gray-800 dark:text-white'>Năng lực học tập</strong>{' '}
                        (Thi thử) = Tư vấn sát thực tế nhất.
                      </span>
                    </li>
                    <li className='flex items-start text-gray-600 dark:text-gray-300'>
                      <span className='material-icons-outlined text-green-500 text-sm mr-2 mt-1'>
                        check_circle
                      </span>
                      <span>Khuyến nghị thi lặp lại 3 lần để đảm bảo độ ổn định của kết quả.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className='text-center'>
                <Link
                  to='/thi-thu-thpt'
                  className='inline-flex items-center px-8 py-3 rounded-lg bg-primary hover:bg-primary-hover text-white text-base font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 uppercase'
                >
                  LÀM BÀI THI THỬ NGAY
                  <span className='material-icons-outlined ml-2'>arrow_forward</span>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // INSTRUCTION SCREEN - New Design from HTML
  if (currentScreen === 'instruction') {
    return (
      <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body min-h-screen flex flex-col transition-colors duration-200'>
        {seoElements}
        <UnifiedHeader />
        <main className='pt-14 pb-12 flex-1'>
          <section className='bg-gradient-to-b from-blue-50/60 to-white dark:from-gray-800 dark:to-background-dark'>
            <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14 space-y-10'>
              <div className='text-center'>
                <span className='inline-block py-1 px-3 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs font-bold tracking-wide uppercase mb-3'>
                  Hướng dẫn làm bài
                </span>
                <h2 className='text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white mb-3'>
                  Khám phá tiềm năng nghề nghiệp của bạn
                </h2>
                <p className='text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto'>
                  Bài đánh giá do IEQTE phát triển, ứng dụng lý thuyết Holland giúp bạn định hướng
                  tương lai chính xác hơn.
                </p>
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-12 gap-8'>
                {/* Steps */}
                <div className='lg:col-span-7 space-y-4'>
                  <div className='bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm'>
                    <div className='flex gap-4'>
                      <div className='flex-shrink-0 w-12 h-12 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-xl flex items-center justify-center'>
                        1
                      </div>
                      <div className='space-y-2'>
                        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                          Đọc kỹ mô tả
                        </h3>
                        <p className='text-gray-600 dark:text-gray-300 leading-relaxed'>
                          Bài test gồm{' '}
                          <strong className='text-gray-900 dark:text-white'>48 câu hỏi</strong> mô
                          tả về sở thích và hoạt động. Hãy đọc kỹ từng nội dung được đưa ra.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm'>
                    <div className='flex gap-4'>
                      <div className='flex-shrink-0 w-12 h-12 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-xl flex items-center justify-center'>
                        2
                      </div>
                      <div className='space-y-2'>
                        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                          Lựa chọn CÓ hoặc KHÔNG
                        </h3>
                        <p className='text-gray-600 dark:text-gray-300 leading-relaxed'>
                          Với mỗi mô tả, chọn{' '}
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 mx-1'>
                            CÓ
                          </span>
                          nếu bạn thích/đồng ý hoặc{' '}
                          <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 mx-1'>
                            KHÔNG
                          </span>
                          nếu ngược lại.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm'>
                    <div className='flex gap-4'>
                      <div className='flex-shrink-0 w-12 h-12 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-xl flex items-center justify-center'>
                        3
                      </div>
                      <div className='space-y-2'>
                        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                          Không có đáp án đúng sai
                        </h3>
                        <p className='text-gray-600 dark:text-gray-300 leading-relaxed'>
                          Đây là bài đánh giá về chính bạn. Không có câu trả lời nào là sai cả. Hãy
                          thoải mái thể hiện quan điểm cá nhân.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className='lg:col-span-5 space-y-4'>
                  <div className='bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 shadow-sm'>
                    <div className='flex items-start gap-3'>
                      <div className='mt-1 text-amber-600 dark:text-amber-300'>
                        <Lightbulb className='w-6 h-6' />
                      </div>
                      <div className='space-y-2'>
                        <div className='text-amber-700 dark:text-amber-300 font-bold uppercase text-xs tracking-wide'>
                          Lưu ý quan trọng
                        </div>
                        <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
                          Trung thực với bản thân
                        </h3>
                        <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                          Kết quả chính xác nhất chỉ đến khi bạn trả lời dựa trên suy nghĩ của chính
                          mình.
                        </p>
                        <p className='text-gray-900 dark:text-white font-medium italic border-l-4 border-amber-400 pl-3 py-1'>
                          "Hãy trả lời trung thực theo bản thân bạn, không theo ý người khác mong
                          muốn."
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 p-6 shadow-sm divide-y divide-gray-100 dark:divide-gray-800'>
                    <div className='flex items-center justify-between py-3'>
                      <span className='text-gray-500 dark:text-gray-400 flex items-center gap-2'>
                        <Loader2 className='h-4 w-4 animate-spin text-primary' />
                        Thời gian ước tính
                      </span>
                      <span className='font-semibold text-gray-900 dark:text-white'>~5 Phút</span>
                    </div>
                    <div className='flex items-center justify-between py-3'>
                      <span className='text-gray-500 dark:text-gray-400 flex items-center gap-2'>
                        <ListOrdered className='h-4 w-4' />
                        Số lượng câu hỏi
                      </span>
                      <span className='font-semibold text-gray-900 dark:text-white'>48 Câu</span>
                    </div>
                    <div className='flex items-center justify-between py-3'>
                      <span className='text-gray-500 dark:text-gray-400 flex items-center gap-2'>
                        <Target className='h-4 w-4' />
                        Lý thuyết nền tảng
                      </span>
                      <span className='font-semibold text-gray-900 dark:text-white'>Holland</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleBeginQuestions}
                    disabled={loadingGroups}
                    className='w-full h-12 text-base font-semibold shadow-md'
                  >
                    {loadingGroups ? (
                      <>
                        <Loader2 className='h-5 w-5 animate-spin' />
                        Đang tải...
                      </>
                    ) : (
                      <>
                        Tiếp theo
                        <ArrowRight className='h-4 w-4' />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>
        <UnifiedFooter />
      </div>
    );
  }

  // QUESTIONS SCREEN - New Design (matches cat_assessment_flow)
  if (currentScreen === 'questions') {
    if (!currentGroup) {
      return (
        <div className='min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center px-4'>
          <div className='text-center space-y-4'>
            <AlertCircle className='h-10 w-10 text-destructive mx-auto' />
            <p className='text-destructive font-semibold text-lg'>Không tìm thấy nhóm câu hỏi</p>
            <Button
              onClick={() => updateScreen('intro')}
              variant='outline'
              className='mt-2 border-border-light dark:border-border-dark'
            >
              Quay lại
            </Button>
          </div>
        </div>
      );
    }

    const answeredCount =
      currentGroup.questions?.filter(q => answers[q.id] !== undefined).length || 0;
    const totalQuestions = currentGroup.questions?.length || 0;
    const totalProgress = Math.round(((currentGroupIndex + 1) / Math.max(groups.length, 1)) * 100);

    return (
      <div className='font-sans antialiased text-text-light dark:text-text-dark bg-background-light dark:bg-background-dark min-h-screen flex flex-col transition-colors duration-300'>
        {seoElements}
        <UnifiedHeader />
        <main className='pt-14 pb-12 flex-1'>
          {/* Main */}
          <div className='flex-grow flex flex-col md:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 gap-8'>
            {/* Sidebar progress - fixed position */}
            <aside className='w-full md:w-64 flex-shrink-0 hidden md:block relative'>
              <div className='fixed top-20 w-64 bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark p-5 shadow-sm max-h-[calc(100vh-6rem)] overflow-y-auto z-10'>
                <h3 className='font-semibold text-lg mb-4 text-text-light dark:text-text-dark'>
                  Tiến độ bài thi
                </h3>
                <div className='relative pl-4 border-l-2 border-border-light dark:border-border-dark space-y-6'>
                  {groups.map((group, index) => {
                    const isCurrent = index === currentGroupIndex;
                    const isDone = index < currentGroupIndex;
                    const dotColor = isCurrent
                      ? 'bg-primary'
                      : isDone
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600';
                    const textColor = isCurrent
                      ? 'text-primary'
                      : isDone
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-text-muted-light dark:text-text-muted-dark';
                    const titleColor =
                      isCurrent || isDone
                        ? 'text-text-light dark:text-text-dark'
                        : 'text-text-muted-light dark:text-text-muted-dark';

                    return (
                      <div
                        key={group.id}
                        className={`relative ${isCurrent ? '' : 'group cursor-pointer'} ${
                          isDone ? '' : 'opacity-80'
                        }`}
                      >
                        <div
                          className={`absolute -left-[21px] top-1 h-3 w-3 rounded-full ${dotColor} ring-4 ring-white dark:ring-surface-dark`}
                        />
                        <p className={`text-xs font-semibold mb-0.5 ${textColor}`}>
                          Nhóm {index + 1}
                          {isCurrent ? ' (Đang làm)' : isDone ? ' (Hoàn thành)' : ''}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className='mt-8 pt-6 border-t border-border-light dark:border-border-dark'>
                  <div className='flex justify-between text-xs mb-2'>
                    <span className='text-text-muted-light dark:text-text-muted-dark'>
                      Tổng quan
                    </span>
                    <span className='font-bold text-primary'>{totalProgress}%</span>
                  </div>
                  <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5'>
                    <div
                      className='bg-primary h-1.5 rounded-full transition-all'
                      style={{ width: `${totalProgress}%` }}
                    />
                  </div>

                  {/* Auto-save indicator */}
                  <div className='mt-4 flex items-center gap-2 text-xs'>
                    {autoSaveStatus === 'saving' && (
                      <>
                        <Loader2 className='h-3 w-3 animate-spin text-blue-500' />
                        <span className='text-blue-500'>Đang lưu...</span>
                      </>
                    )}
                    {autoSaveStatus === 'saved' && (
                      <>
                        <CheckCircle2 className='h-3 w-3 text-green-500' />
                        <span className='text-green-500'>Đã lưu tự động</span>
                      </>
                    )}
                    {autoSaveStatus === 'idle' && (
                      <>
                        <span className='material-icons-outlined text-xs text-text-muted-light dark:text-text-muted-dark'>
                          cloud_done
                        </span>
                        <span className='text-text-muted-light dark:text-text-muted-dark'>
                          Tự động lưu
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main content */}
            <section className='flex-grow'>
              {/* Mobile progress */}
              <div className='md:hidden mb-6'>
                <div className='flex items-center justify-between mb-2'>
                  <span className='text-sm font-bold text-text-light dark:text-text-dark'>
                    Nhóm {currentGroupIndex + 1}
                  </span>
                  <span className='text-xs text-text-muted-light dark:text-text-muted-dark'>
                    {answeredCount}/{totalQuestions} đã trả lời
                  </span>
                </div>
                <div className='flex gap-1'>
                  {groups.map((_, idx) => {
                    const state =
                      idx < currentGroupIndex
                        ? 'bg-green-500'
                        : idx === currentGroupIndex
                          ? 'bg-primary'
                          : 'bg-gray-200 dark:bg-gray-700';
                    return <div key={idx} className={`h-1 flex-1 rounded-full ${state}`} />;
                  })}
                </div>
              </div>

              <div className='mb-8'>
                <h1 className='text-3xl font-bold text-text-light dark:text-text-dark mb-2'>
                  Nhóm {currentGroupIndex + 1}
                </h1>
                <p className='text-text-muted-light dark:text-text-muted-dark text-lg leading-relaxed'>
                  Hãy đánh dấu vào những hoạt động hoặc sở thích mà bạn cảm thấy{' '}
                  <strong className='text-primary'>hứng thú</strong> hoặc{' '}
                  <strong className='text-primary'>có khả năng làm tốt</strong>. Không cần suy nghĩ
                  quá lâu, hãy chọn theo trực giác đầu tiên của bạn.
                </p>
              </div>

              <div className='bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-border-light dark:border-border-dark overflow-hidden'>
                <div
                  className='hidden md:grid bg-gray-50 dark:bg-gray-800 border-b border-border-light dark:border-border-dark px-6 py-4'
                  style={{ gridTemplateColumns: '1fr 96px 96px' }}
                >
                  <div className='font-semibold text-sm text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider'>
                    Nội dung câu hỏi
                  </div>
                  <div className='font-semibold text-sm text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider text-center'>
                    Có
                  </div>
                  <div className='font-semibold text-sm text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider text-center'>
                    Không
                  </div>
                </div>
                <div className='divide-y divide-border-light dark:divide-border-dark'>
                  {(currentGroup.questions || []).map((q, idx) => {
                    const currentAnswers = getCurrentGroupAnswers();
                    const value = currentAnswers[q.id];
                    const isUnanswered = value === undefined;
                    const rowAccent =
                      isUnanswered && !isCurrentGroupComplete
                        ? 'bg-orange-50/50 dark:bg-orange-900/10 border-l-4 border-primary'
                        : '';

                    const renderOption = (isYes: boolean) => {
                      const checked = value === isYes;
                      const base =
                        'w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200';
                      const checkedStyle = checked
                        ? isYes
                          ? 'bg-green-50 border-green-500 dark:bg-green-950/20 dark:border-green-400'
                          : 'bg-red-50 border-red-500 dark:bg-red-950/20 dark:border-red-400'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary/50';
                      const iconColor = checked
                        ? isYes
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                        : 'text-gray-300 group-hover:text-primary/50';
                      return (
                        <label className='relative inline-flex items-center cursor-pointer group'>
                          <input
                            className='sr-only peer'
                            name={q.id}
                            type='radio'
                            checked={checked || false}
                            onChange={() =>
                              handleAnswerChange(currentGroup.id, {
                                [q.id]: isYes,
                              })
                            }
                          />
                          <div className={`${base} ${checkedStyle}`}>
                            <span
                              className={`material-icons-outlined text-xl font-semibold ${iconColor}`}
                            >
                              {isYes ? 'check' : 'close'}
                            </span>
                          </div>
                          <span className='md:hidden ml-2 text-sm font-medium'>
                            {isYes ? 'Có' : 'Không'}
                          </span>
                        </label>
                      );
                    };

                    return (
                      <div
                        key={q.id}
                        className={`p-4 md:px-6 md:py-5 flex flex-col md:grid md:items-center transition-colors ${rowAccent}`}
                        style={{ gridTemplateColumns: '1fr 96px 96px' }}
                      >
                        <div className='pr-4 mb-3 md:mb-0'>
                          <span className='text-xs font-bold text-text-muted-light dark:text-text-muted-dark mr-2 md:hidden'>
                            {String(idx + 1).padStart(2, '0')}.
                          </span>
                          <span className='text-base font-medium text-text-light dark:text-text-dark'>
                            {q.statement || q.label}
                          </span>
                        </div>
                        <div className='hidden md:flex justify-center'>{renderOption(true)}</div>
                        <div className='hidden md:flex justify-center'>{renderOption(false)}</div>
                        {/* Mobile: show both options in a row */}
                        <div className='flex md:hidden justify-end gap-4'>
                          {renderOption(true)}
                          {renderOption(false)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!isCurrentGroupComplete && (
                <div className='mt-4 flex items-center justify-end text-red-500 text-sm gap-2'>
                  <span className='material-icons-outlined text-sm'>info</span>
                  <span>Vui lòng hoàn thành tất cả câu hỏi trong nhóm để tiếp tục.</span>
                </div>
              )}

              <div className='mt-8 flex justify-between items-center pb-12'>
                <button
                  onClick={handlePreviousGroup}
                  disabled={currentGroupIndex === 0}
                  className='flex items-center gap-2 px-6 py-3 rounded-lg border border-border-light dark:border-border-dark font-medium text-text-muted-light dark:text-text-muted-dark hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <span className='material-icons-outlined text-sm transform rotate-180'>
                    arrow_forward
                  </span>
                  Quay lại
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!isCurrentGroupComplete}
                  className='flex items-center gap-2 px-8 py-3 rounded-lg font-bold shadow-none transition-all disabled:cursor-not-allowed disabled:opacity-60 bg-primary text-white hover:bg-primary-hover'
                >
                  {currentGroupIndex === groups.length - 1 ? 'Hoàn thành' : 'Tiếp theo'}
                  <span className='material-icons-outlined text-sm'>arrow_forward</span>
                </button>
              </div>
            </section>
          </div>
        </main>
        <UnifiedFooter />

        {/* Navigation confirmation dialog */}
        <AlertDialog open={showNavigationConfirm} onOpenChange={setShowNavigationConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Xác nhận rời khỏi bài thi</AlertDialogTitle>
              <AlertDialogDescription>
                Bạn đang có câu trả lời chưa hoàn thành. Nếu rời khỏi trang này, tất cả câu trả lời
                đã lưu sẽ vẫn được giữ lại nhưng bạn cần hoàn thành bài thi sau.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancelNavigation}>Hủy</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmNavigation}
                className='bg-red-500 hover:bg-red-600'
              >
                Rời khỏi
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Loading Overlay */}
        <AnimatePresence>
          {isSubmitting && <LoadingOverlay message={loadingMessage} />}
        </AnimatePresence>
      </div>
    );
  }

  // FREE RESULT SCREEN
  if (currentScreen === 'free-result' || currentScreen === 'payment') {
    const isPaymentScreen = currentScreen === 'payment';
    const topGroup = topGroups[0];
    const topGroupData = groups.find(g => g.id === topGroup?.id);

    // Map Holland codes
    // Helper to get Holland info from group
    const getHollandInfo = (group: CareerOrientationGroupWithQuestions | null) => {
      if (!group) return null;

      const codeMap: Record<string, any> = {
        group1: {
          code: 'R',
          name: 'Kỹ thuật (Realistic)',
          color: 'red',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          textColor: 'text-red-600',
        },
        group2: {
          code: 'I',
          name: 'Nghiên cứu (Investigative)',
          color: 'blue',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          textColor: 'text-blue-600',
        },
        group3: {
          code: 'A',
          name: 'Nghệ thuật (Artistic)',
          color: 'orange',
          bgColor: 'bg-orange-50 dark:bg-orange-900/20',
          textColor: 'text-orange-600',
        },
        group4: {
          code: 'S',
          name: 'Xã hội (Social)',
          color: 'green',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          textColor: 'text-green-600',
        },
        group5: {
          code: 'E',
          name: 'Quản lý (Enterprising)',
          color: 'purple',
          bgColor: 'bg-purple-50 dark:bg-purple-900/20',
          textColor: 'text-purple-600',
        },
        group6: {
          code: 'C',
          name: 'Nghiệp vụ (Conventional)',
          color: 'gray',
          bgColor: 'bg-gray-100 dark:bg-gray-700/50',
          textColor: 'text-gray-600',
        },
      };

      // Try by ID first
      if (codeMap[group.id]) return codeMap[group.id];

      // Fallback mappings
      if (
        group.name?.toUpperCase().includes('NHÓM 1') ||
        group.name?.toUpperCase().includes('KỸ THUẬT')
      )
        return codeMap['group1'];
      if (
        group.name?.toUpperCase().includes('NHÓM 2') ||
        group.name?.toUpperCase().includes('NGHIÊN CỨU')
      )
        return codeMap['group2'];
      if (
        group.name?.toUpperCase().includes('NHÓM 3') ||
        group.name?.toUpperCase().includes('NGHỆ THUẬT')
      )
        return codeMap['group3'];
      if (
        group.name?.toUpperCase().includes('NHÓM 4') ||
        group.name?.toUpperCase().includes('XÃ HỘI')
      )
        return codeMap['group4'];
      if (
        group.name?.toUpperCase().includes('NHÓM 5') ||
        group.name?.toUpperCase().includes('QUẢN LÝ')
      )
        return codeMap['group5'];
      if (
        group.name?.toUpperCase().includes('NHÓM 6') ||
        group.name?.toUpperCase().includes('NGHIỆP VỤ')
      )
        return codeMap['group6'];

      return {
        code: '?',
        name: group.name || 'Unknown',
        color: 'gray',
        bgColor: 'bg-gray-50',
        textColor: 'text-gray-600',
      };
    };

    // Get all groups except top 1 for locked display
    const lockedGroups = groups
      .filter(g => g.id !== topGroup?.id)
      .slice(0, 5)
      .map(g => ({
        ...g,
        holland: getHollandInfo(g) || {
          code: '?',
          name: 'Unknown',
          color: 'gray',
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-600',
        },
      }));

    // Get top group Holland info
    const topHolland = getHollandInfo(topGroupData as any);

    return (
      <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
        {seoElements}
        <UnifiedHeader />
        <main className='pt-14 pb-12 flex-1'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12'>
            {/* Header with celebration icon - Enhanced */}
            <div className='relative mb-10'>
              {/* Gradient background */}
              <div className='absolute inset-0 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 dark:from-orange-950/20 dark:via-amber-950/20 dark:to-orange-950/20 rounded-3xl -z-10' />
              <div className='relative p-8 md:p-10 text-center'>
                {/* Animated celebration icon */}
                <div className='inline-block mb-6 animate-bounce-slow'>
                  <div className='relative'>
                    {/* Outer pulse ring */}
                    <div className='absolute inset-0 bg-accent-yellow/20 rounded-full animate-ping' />
                    {/* Icon container */}
                    <div className='relative p-5 bg-gradient-to-br from-accent-yellow to-orange-500 rounded-full shadow-lg shadow-orange-500/50'>
                      <span className='material-symbols-outlined text-5xl md:text-6xl text-white drop-shadow-lg animate-wiggle'>
                        celebration
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main heading with gradient text */}
                <h1 className='text-4xl md:text-5xl font-extrabold mb-4 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 bg-clip-text text-transparent drop-shadow-sm'>
                  Chúc mừng bạn đã hoàn thành bài đánh giá!
                </h1>

                {/* Subtitle */}
                <p className='text-base md:text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed font-medium'>
                  Dưới đây là{' '}
                  <span className='font-bold text-orange-600 dark:text-orange-400'>
                    nhóm tính cách nổi bật nhất
                  </span>{' '}
                  của bạn. Để khám phá bức tranh toàn diện về bản thân và nhận định hướng chi tiết,
                  hãy nâng cấp lên{' '}
                  <span className='font-bold text-primary'>
                    Báo cáo kết quả và tư vấn hướng nghiệp
                  </span>
                  .
                </p>
              </div>
            </div>

            {/* Top Group Card - Enhanced */}
            {topGroup && topGroupData && topHolland && (
              <div className='relative mb-10 group'>
                {/* Animated gradient border */}
                <div className='absolute -inset-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 rounded-2xl opacity-30 group-hover:opacity-50 blur-sm transition-opacity duration-500 animate-gradient-xy' />

                {/* Main card */}
                <div className='relative bg-white dark:bg-card-dark rounded-xl border-2 border-orange-200 dark:border-orange-800 shadow-2xl shadow-orange-500/20 overflow-hidden'>
                  {/* Decorative background pattern */}
                  <div className='absolute inset-0 opacity-5 dark:opacity-10'>
                    <div className='absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-500 to-red-500 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2' />
                    <div className='absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-amber-500 to-orange-500 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2' />
                  </div>

                  {/* Trophy/Star badge */}
                  <div className='absolute top-4 right-4 z-10'>
                    <div className='relative'>
                      <div className='absolute inset-0 bg-orange-400 rounded-full blur-md animate-pulse' />
                      <div className='relative bg-gradient-to-br from-yellow-400 to-orange-500 p-3 rounded-full shadow-lg'>
                        <span className='material-symbols-outlined text-2xl text-white'>stars</span>
                      </div>
                    </div>
                  </div>

                  <div className='relative p-8 md:p-10'>
                    <div className='flex flex-col md:flex-row gap-8 items-center'>
                      {/* Holland Code Badge - Larger and more prominent */}
                      <div className='flex-shrink-0'>
                        <div className='relative group/code'>
                          {/* Glow effect */}
                          <div
                            className={`absolute -inset-2 ${topHolland?.bgColor || 'bg-gray-100'} rounded-3xl opacity-40 blur-xl group-hover/code:opacity-60 transition-opacity`}
                          />

                          <div
                            className={`relative w-36 h-36 md:w-40 md:h-40 p-3 rounded-3xl ${
                              topHolland?.bgColor || 'bg-gray-100'
                            } border-4 border-white dark:border-gray-700 shadow-2xl flex flex-col items-center justify-center ${
                              topHolland?.textColor || 'text-gray-600'
                            } font-bold transform transition-transform duration-300 group-hover/code:scale-105`}
                          >
                            <span className='text-6xl md:text-7xl mb-2 drop-shadow-lg'>
                              {topHolland?.code || '?'}
                            </span>
                            <span className='text-xs font-bold uppercase tracking-wider text-center leading-tight'>
                              {topHolland?.name?.split('(')[0].trim() || 'Unknown'}
                            </span>
                            <span className='text-[10px] opacity-75 mt-1'>
                              {topHolland?.name?.includes('(')
                                ? '(' + topHolland?.name?.split('(')[1]
                                : ''}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className='flex-1 text-center md:text-left'>
                        {/* Label */}
                        <div className='inline-flex items-center gap-2 mb-3 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-full'>
                          <span className='material-symbols-outlined text-orange-600 dark:text-orange-400 text-xl'>
                            emoji_events
                          </span>
                          <span className='text-sm font-bold text-orange-800 dark:text-orange-300 uppercase tracking-wide'>
                            Kết quả nổi bật
                          </span>
                        </div>

                        {/* Title with score */}
                        <div className='mb-4'>
                          <h2 className='text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white mb-3 leading-tight'>
                            Nhóm tính cách nổi bật
                          </h2>
                          <div className='flex items-center justify-center md:justify-start gap-3 flex-wrap'>
                            <span className='text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent'>
                              {topGroupData?.name || 'N/A'}
                            </span>
                            <span className='px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/50 animate-pulse-slow'>
                              ⭐ Điểm: {topGroup.score}/8
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        <p className='text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed font-medium mb-6'>
                          {topGroupData?.shortDesc ||
                            topGroupData?.fullDesc ||
                            'Nhóm tính cách phù hợp nhất với bạn.'}
                        </p>

                        {/* Teaser Bullet Points - to encourage CTA */}
                        {topGroupTeaserTitles.length > 0 && (
                          <div className='mt-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 rounded-xl border border-orange-200/50 dark:border-orange-800/30'>
                            <div className='flex items-center gap-2 mb-3'>
                              <span className='material-symbols-outlined text-orange-600 dark:text-orange-400 text-lg'>
                                auto_awesome
                              </span>
                              <span className='text-sm font-bold text-orange-800 dark:text-orange-300 uppercase tracking-wide'>
                                Điểm nổi bật của bạn
                              </span>
                            </div>
                            <ul className='space-y-2'>
                              {topGroupTeaserTitles.slice(0, 4).map((title, idx) => (
                                <li
                                  key={idx}
                                  className='flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300'
                                >
                                  <span className='flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center mt-0.5'>
                                    {idx + 1}
                                  </span>
                                  <span className='font-medium leading-relaxed'>{title}</span>
                                </li>
                              ))}
                            </ul>
                            <div className='mt-4 pt-3 border-t border-orange-200/50 dark:border-orange-700/30'>
                              <p className='text-xs text-orange-700 dark:text-orange-300 italic flex items-center gap-1'>
                                <span className='material-symbols-outlined text-sm'>lock</span>
                                Mở khóa báo cáo đầy đủ để xem phân tích chi tiết về từng đặc điểm
                                này
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Locked Groups Grid */}
            <div className='mb-8'>
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4'>
                {lockedGroups.map(group => (
                  <div
                    key={group.id}
                    className='relative group/code group/locked w-full h-full aspect-square flex flex-col items-center justify-center'
                  >
                    {/* Glow effect */}
                    <div
                      className={`absolute -inset-2 ${group.holland?.bgColor || 'bg-gray-100'} rounded-3xl opacity-40 blur-xl group-hover/code:opacity-60 transition-opacity`}
                    />

                    {/* Card Holland code with blur */}
                    <div
                      className={`relative w-full h-full p-3 rounded-3xl blur-[2px] ${
                        group.holland?.bgColor || 'bg-gray-100'
                      } border-4 border-white dark:border-gray-700 shadow-2xl flex flex-col items-center justify-center ${
                        group.holland?.textColor || 'text-gray-600'
                      } font-bold transform transition-transform duration-300 group-hover/code:scale-105`}
                    >
                      <span className='text-6xl md:text-7xl mb-2 drop-shadow-lg'>
                        {group.holland?.code || '?'}
                      </span>
                      <span className='text-xs font-bold uppercase tracking-wider text-center leading-tight'>
                        {group.holland?.name?.split('(')[0].trim() || 'Unknown'}
                      </span>
                      <span className='text-[10px] opacity-75 mt-1'>
                        {group.holland?.name?.includes('(')
                          ? '(' + group.holland?.name?.split('(')[1]
                          : ''}
                      </span>
                    </div>

                    {/* Lock overlay - not blurred */}
                    <div className='absolute inset-0 bg-white/60 dark:bg-black/60 flex flex-col items-center justify-center z-10 rounded-3xl'>
                      <span className='material-symbols-outlined text-3xl sm:text-4xl text-subtext-light dark:text-subtext-dark mb-2'>
                        lock
                      </span>
                      <span className='text-xs font-bold text-subtext-light dark:text-subtext-dark'>
                        Mở khóa
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section with Level Selection */}
            <div className='bg-gradient-to-br from-accent-yellow/10 via-background-light to-background-light dark:from-accent-yellow/10 dark:via-background-dark dark:to-background-dark p-6 sm:p-8 rounded-2xl border border-accent-yellow/20 text-center'>
              <h3 className='text-xl font-bold text-text-light dark:text-white mb-3'>
                Mở khóa ngay để khám phá tính cách, sở thích và nhận gợi ý ngành, nghề phù hợp ở bậc
                đại học.
              </h3>
              <div className='grid grid-cols-1 gap-4 text-left max-w-xl mx-auto mb-8'>
                <div className='flex items-start gap-3 p-4 bg-white dark:bg-card-dark/50 rounded-lg shadow-sm hover:shadow-md transition-shadow'>
                  <span className='material-symbols-outlined text-primary text-xl flex-shrink-0 mt-0.5'>
                    check_circle
                  </span>
                  <span className='text-sm font-medium leading-relaxed'>
                    Phân tích chi tiết tính cách, sở thích và đưa ra gợi ý các ngành, nghề phù hợp
                    nhất với bạn
                  </span>
                </div>
                <div className='flex items-start gap-3 p-4 bg-white dark:bg-card-dark/50 rounded-lg shadow-sm hover:shadow-md transition-shadow'>
                  <span className='material-symbols-outlined text-primary text-xl flex-shrink-0 mt-0.5'>
                    check_circle
                  </span>
                  <span className='text-sm font-medium leading-relaxed'>
                    Gợi ý cụ thể các ngành nghề học ở bậc đại học của các trường đại học hàng đầu
                  </span>
                </div>
                <div className='flex items-start gap-3 p-4 bg-white dark:bg-card-dark/50 rounded-lg shadow-sm hover:shadow-md transition-shadow'>
                  <span className='material-symbols-outlined text-primary text-xl flex-shrink-0 mt-0.5'>
                    check_circle
                  </span>
                  <span className='text-sm font-medium leading-relaxed'>
                    Báo cáo chi tiết từ 3-5 trang với phân tích sâu và đầy đủ, giúp bạn có cái nhìn
                    toàn diện về định hướng nghề nghiệp
                  </span>
                </div>
              </div>
              <div className='mb-6'>
                <button
                  onClick={() => setShowLevelSelection(true)}
                  className='w-full sm:w-auto px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-primary to-orange-600 rounded-xl hover:from-primary-hover hover:to-orange-700 shadow-lg shadow-orange-500/30 transition-all transform hover:scale-105 uppercase tracking-wide flex items-center justify-center gap-2 mx-auto'
                >
                  <span className='material-symbols-outlined text-2xl'>lock_open</span>
                  MỞ KHOÁ BÁO CÁO ĐẦY ĐỦ NGAY
                </button>
                <p className='mt-3 text-sm text-subtext-light dark:text-subtext-dark'>
                  Chỉ mất 2 phút để thanh toán & nhận kết quả ngay
                </p>
              </div>
              <button
                onClick={handleRestart}
                className='text-sm font-medium text-subtext-light dark:text-subtext-dark hover:text-primary dark:hover:text-primary-hover transition-colors'
              >
                Làm lại bài đánh giá
              </button>
            </div>
          </div>
        </main>
        <UnifiedFooter />

        {/* Level Selection Modal */}
        <AlertDialog open={showLevelSelection} onOpenChange={setShowLevelSelection}>
          <AlertDialogContent className='max-w-3xl'>
            <AlertDialogHeader className='text-center'>
              <AlertDialogTitle className='text-2xl font-bold mb-2'>
                Bạn cần báo cáo tư vấn chi tiết chọn ngành học đại học hoặc học nghề phù hợp, xin
                vui lòng lựa chọn
              </AlertDialogTitle>
              <AlertDialogDescription>
                Hãy chọn loại báo cáo phù hợp với nhu cầu của bạn
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6 py-6'>
              <button
                onClick={async () => {
                  const level = 'THCS';
                  setSelectedLevel(level);
                  setShowLevelSelection(false);
                  if (assessmentId) {
                    try {
                      await apiService.updateCareerOrientationEducationLevel(assessmentId, level);
                    } catch (error: any) {
                      console.error('Failed to update education level:', error);
                    }
                  }
                  // Proceed to payment or bypass
                  if (isTestBypassActive && assessmentId) {
                    // Bypass payment - grant access directly
                    try {
                      toast.success('Chế độ kiểm thử IEQTE: Bỏ qua thanh toán');
                      await apiService.grantCareerOrientationFullReportAccess(assessmentId);
                      const fullReport =
                        await apiService.getCareerOrientationFullReport(assessmentId);
                      const fullReportGroups = fullReport.groupResults
                        .sort((a: any, b: any) => a.rank - b.rank)
                        .map((gr: any) => ({
                          ...gr.group,
                          questions: gr.group.questions || [],
                        }));
                      setFullReportData(fullReportGroups);
                      updateScreen('full-report', assessmentId);
                    } catch (error) {
                      console.error('[CareerOrientation] Bypass grant failed:', error);
                      toast.error('Không thể kích hoạt chế độ kiểm thử. Vui lòng thử lại.');
                    }
                  } else {
                    setIsPaymentDialogOpen(true);
                  }
                }}
                className='group relative p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-primary transition-all text-left flex flex-col gap-4 hover:shadow-lg'
              >
                <div className='w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform'>
                  <School className='w-6 h-6' />
                </div>
                <div>
                  <h4 className='text-lg font-bold text-gray-900 dark:text-white mb-1'>
                    Chọn học nghề
                  </h4>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    Tư vấn định hướng nghề nghiệp và các ngành nghề phù hợp với bạn.
                  </p>
                </div>
                <div className='absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600 group-hover:border-primary group-hover:bg-primary transition-colors flex items-center justify-center'>
                  <span className='material-symbols-outlined text-white text-sm opacity-0 group-hover:opacity-100'>
                    check
                  </span>
                </div>
              </button>

              <button
                onClick={async () => {
                  const level = 'THPT';
                  setSelectedLevel(level);
                  setShowLevelSelection(false);
                  if (assessmentId) {
                    try {
                      await apiService.updateCareerOrientationEducationLevel(assessmentId, level);
                    } catch (error: any) {
                      console.error('Failed to update education level:', error);
                    }
                  }
                  // Proceed to payment or bypass
                  if (isTestBypassActive && assessmentId) {
                    // Bypass payment - grant access directly
                    try {
                      toast.success('Chế độ kiểm thử IEQTE: Bỏ qua thanh toán');
                      await apiService.grantCareerOrientationFullReportAccess(assessmentId);
                      const fullReport =
                        await apiService.getCareerOrientationFullReport(assessmentId);
                      const fullReportGroups = fullReport.groupResults
                        .sort((a: any, b: any) => a.rank - b.rank)
                        .map((gr: any) => ({
                          ...gr.group,
                          questions: gr.group.questions || [],
                        }));
                      setFullReportData(fullReportGroups);
                      updateScreen('full-report', assessmentId);
                    } catch (error) {
                      console.error('[CareerOrientation] Bypass grant failed:', error);
                      toast.error('Không thể kích hoạt chế độ kiểm thử. Vui lòng thử lại.');
                    }
                  } else {
                    setIsPaymentDialogOpen(true);
                  }
                }}
                className='group relative p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-accent-blue dark:hover:border-accent-blue transition-all text-left flex flex-col gap-4 hover:shadow-lg'
              >
                <div className='w-12 h-12 rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform'>
                  <GraduationCap className='w-6 h-6' />
                </div>
                <div>
                  <h4 className='text-lg font-bold text-gray-900 dark:text-white mb-1'>
                    Chọn ngành học đại học
                  </h4>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    Tư vấn chọn ngành học đại học và định hướng nghề nghiệp chuyên sâu.
                  </p>
                </div>
                <div className='absolute top-4 right-4 w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600 group-hover:border-accent-blue group-hover:bg-accent-blue transition-colors flex items-center justify-center'>
                  <span className='material-symbols-outlined text-white text-sm opacity-0 group-hover:opacity-100'>
                    check
                  </span>
                </div>
              </button>
            </div>
            <div className='text-center'>
              <button
                onClick={() => setShowLevelSelection(false)}
                className='text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline'
              >
                Tôi sẽ chọn sau
              </button>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Payment Dialog */}
        <ModernPaymentDialog
          open={isPaymentDialogOpen}
          onOpenChange={setIsPaymentDialogOpen}
          onPaymentSuccess={handlePaymentSuccess}
          assessmentId={assessmentId || undefined}
          selectedLevel={selectedLevel || undefined}
          onSelectLevel={handleSelectLevelForPayment}
          examTitle={`Career Orientation Report - ${selectedLevel === 'THCS' ? 'Vocational' : 'University'}`}
          examTitleVi={`Báo cáo ${selectedLevel === 'THCS' ? 'Định hướng học nghề' : 'Định hướng ngành đại học'}`}
          price={catReportPrice}
        />
        <AnimatePresence>
          {isSubmitting && <LoadingOverlay message={loadingMessage} />}
        </AnimatePresence>

        {/* CSS Animations for Enhanced Free Report */}
        <style>{`
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          @keyframes wiggle {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(-5deg); }
            75% { transform: rotate(5deg); }
          }
          @keyframes pulse-slow {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
          }
          @keyframes gradient-xy {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-bounce-slow {
            animation: bounce-slow 3s ease-in-out infinite;
          }
          .animate-wiggle {
            animation: wiggle 2s ease-in-out infinite;
          }
          .animate-pulse-slow {
            animation: pulse-slow 2s ease-in-out infinite;
          }
          .animate-gradient-xy {
            background-size: 200% 200%;
            animation: gradient-xy 3s ease infinite;
          }
        `}</style>
      </div>
    );
  }

  // Default return - should not reach here
  return <>{seoElements}</>;
}
